/**
 * product.js — product page behaviour.
 * Loaded (deferred) from sections/main-product.liquid.
 *
 * Custom elements:
 *
 *   <kosha-gallery>       (Task 4, arrows Task 8b)
 *     .setMedia(mediaArray)      mediaArray = [{ src, alt, width, height }]
 *     prev/next arrows in both the inline track and the lightbox; clamped at
 *     the ends, keyboard-driven from either state
 *
 *   <kosha-product-form>  (Task 5)
 *     .setProduct(productJson)   productJson = /products/<handle>.js shape
 *     emits `kosha:cart:added`   bubbling + composed, after a successful add
 *
 *   <kosha-sticky-atc>    (Task 5)
 *     mobile bar mirroring the real add-to-cart button; no state of its own
 *
 *   <kosha-siblings>      (Task 6, extended in Task 8b)
 *     instant colour switching — no document fetch, no navigation
 *     emits `kosha:sibling:changed` (bubbling + composed) with the payload
 *     entry as `detail`
 *     also owns, all from the same payload: size-aware swatch availability,
 *     the per-colour Product Details copy, and head reconciliation
 *     (canonical / og:url / og:title / twitter:title / og:price / og:image)
 *
 *   <kosha-upsell>        (Task 7)
 *     .setUpsells(upsellArray)   upsellArray = kosha:sibling:changed
 *                                detail.upsells — it also subscribes to that
 *                                event itself, so no wiring is required
 *     emits `kosha:cart:added`   bubbling + composed, `detail.source` is
 *                                'upsell'
 *
 * `setMedia` and `setProduct` are the hard interfaces Task 6's sibling-colour
 * switch calls. Both rebuild from <template> markup the server rendered, so
 * swapped markup can never drift from the initial render, and both return a
 * boolean rather than throwing when handed something unusable.
 *
 * No modules, no imports — one IIFE, so the deferred script works everywhere
 * and there is no build step.
 *
 * SHARED HELPERS: `cartRoute`, `updateCartCount`, `formatMoney` and
 * `prefersReducedMotion` are one-line delegates to `window.kosha`
 * (assets/global.js, loaded from <head> so it always runs first). See the
 * SHARED HELPERS block there.
 */

(function () {
  'use strict';

  /* Candidate widths for client-rebuilt srcsets. Matches the `main_widths`
     list in snippets/product-gallery.liquid. */
  var SRCSET_WIDTHS = [400, 600, 800, 1000, 1200, 1600];
  /* How many gallery dots may be on screen at once. Seven is the iOS
     home-screen count and it is also what keeps the row's frame inside a
     comfortable inset at 390px: 7 x 24px plus six 8px gaps is 216px, which
     leaves 87px of margin on each side, 22% of the viewport. See
     `windowDots()` and §4 of product.css. */
  var DOT_WINDOW = 7;
  var THUMB_WIDTHS = [80, 120, 160, 200];

  /* Delegate to global.js. See the SHARED HELPERS note in this file's header. */
  function prefersReducedMotion() {
    return window.kosha.prefersReducedMotion();
  }

  /* ==========================================================================
     THE PRODUCT-COLOUR ACCENT SYSTEM

     One product hex in, five values out: the fill, the label that is legible
     on it, the same colour as ink on cream, a hover, and a muted tone for the
     sold-out pills. The owner's instruction was that the button BE the set's
     colour — "if it's a pink set the button should be that same pink as the
     color swatch" — so the fill is the metafield hex and the LABEL adapts,
     rather than the colour being dragged dark until one fixed label is safe.

     sections/main-product.liquid computes all five in Liquid for the first
     paint; this is the same arithmetic for a colour swap, where the hex
     arrives in the payload as `entry.colorHex`.

     THE TWO IMPLEMENTATIONS MUST AGREE TO THE BYTE. A swap that recomputed
     even one step differently would show a visible jog on the button the first
     time a shopper changes colour. Both use round-half-up (Liquid's `round`,
     JS's `Math.round`), both walk the same ladders in the same order, and both
     do the two mixes in the same term order, which is why the clamps are loops
     rather than closed-form scales: a closed form needs a 1/2.4 root, and
     Liquid has neither `pow` nor a square root.

     Full derivation, including where every constant comes from, is in the
     comment header of sections/main-product.liquid. The cart lane's upsell
     pill mirrors this function byte for byte, so a change here is a change in
     three files. In brief:

       source  the product hex, unmixed; #4d2e24 (--color-cocoa) when the
               metafield is missing or malformed
       nudge   while 0.176 < L < 0.208 — the only band where NEITHER label
               clears 4.5:1 — scale by 0.99. Four colours in the catalogue need
               it; the deepest move is nine steps.
       label   cream #fefcfa while L <= 0.176, otherwise warm black #1c110c
       ink     the fill walked down a 0.95 ladder to L <= 0.150, which is
               5.13:1 on cream. This is the colour for anything that has to be
               TEXT or a BOUNDARY, because a pale fill can be neither.
       hover   86% fill + 14% of the label's opposite, so hovering can only
               raise the label's contrast
       muted   74% ink + 26% cream, the sold-out tone
     ========================================================================== */

  var ACCENT_FALLBACK = [77, 46, 36];
  var ACCENT_INK_L_MAX = 0.15;
  /* Solved from 4.5:1 against each label ON THE EXACT sRGB CURVE, then pulled
     inward by 0.00167 — the most the cubic approximation below can differ from
     that curve anywhere in the sRGB cube. Deriving these from the
     approximation's own luminance is what put #568670 at 4.44:1 in the first
     version. Full derivation: the Liquid header, step 2. */
  var ACCENT_CREAM_MAX = 0.176;
  var ACCENT_BLACK_MIN = 0.208;
  var ACCENT_CREAM = [254, 252, 250]; /* --color-cream  #fefcfa */
  var ACCENT_BLACK = [28, 17, 12]; /* the warm black #1c110c  */
  /* 26% of --color-cream, as the literals the Liquid adds, so the two
     languages round the muted mix off byte-identical inputs. */
  var ACCENT_CREAM_26 = [66.04, 65.52, 65.0];

  /** Cubic approximation of the sRGB EOTF; see the block above. */
  function stageLinear(channel) {
    var s = channel / 255;
    return 0.012522878 * s + 0.682171111 * s * s + 0.305306011 * s * s * s;
  }

  function stageLuminance(rgb) {
    return (
      0.2126 * stageLinear(rgb[0]) +
      0.7152 * stageLinear(rgb[1]) +
      0.0722 * stageLinear(rgb[2])
    );
  }

  function stageHex(rgb) {
    return (
      '#' +
      rgb
        .map(function (channel) {
          return ('0' + channel.toString(16)).slice(-2);
        })
        .join('')
    );
  }

  /**
   * @param {string} hex `#rgb` or `#rrggbb`. Anything else returns the cocoa
   *   fallback, which is what Liquid renders for a product with no metafield.
   * @returns {{accent: string, on: string, ink: string, hover: string,
   *   muted: string}} five `#rrggbb` strings.
   */
  function accentSystem(hex) {
    var rgb = ACCENT_FALLBACK.slice();
    var raw = typeof hex === 'string' ? hex.trim().toLowerCase() : '';
    var step;

    if (/^#[0-9a-f]{3}$/.test(raw)) {
      raw = '#' + raw[1] + raw[1] + raw[2] + raw[2] + raw[3] + raw[3];
    }

    if (/^#[0-9a-f]{6}$/.test(raw)) {
      rgb = [
        parseInt(raw.slice(1, 3), 16),
        parseInt(raw.slice(3, 5), 16),
        parseInt(raw.slice(5, 7), 16),
      ];
    }

    /* The dead-zone nudge. `luminance` survives the loop and is what the label
       is chosen from, exactly as `pdpdark_l` does in the Liquid. */
    var luminance = stageLuminance(rgb);
    for (step = 0; step < 24; step += 1) {
      luminance = stageLuminance(rgb);
      if (luminance <= ACCENT_CREAM_MAX || luminance >= ACCENT_BLACK_MIN) break;
      rgb = rgb.map(function (channel) {
        return Math.round(channel * 0.99);
      });
    }

    var cream = luminance <= ACCENT_CREAM_MAX;
    var toward = cream ? ACCENT_BLACK : ACCENT_CREAM;

    var hover = rgb.map(function (channel, i) {
      return Math.round(channel * 0.86 + toward[i] * 0.14);
    });

    var ink = rgb.slice();
    for (step = 0; step < 24; step += 1) {
      if (stageLuminance(ink) <= ACCENT_INK_L_MAX) break;
      ink = ink.map(function (channel) {
        return Math.round(channel * 0.95);
      });
    }

    var muted = ink.map(function (channel, i) {
      return Math.round(channel * 0.74 + ACCENT_CREAM_26[i]);
    });

    return {
      accent: stageHex(rgb),
      on: cream ? '#fefcfa' : '#1c110c',
      ink: stageHex(ink),
      hover: stageHex(hover),
      muted: stageHex(muted),
    };
  }

  /**
   * Rebuild a srcset from a Shopify CDN URL by swapping its `width` query
   * parameter. Liquid renders gallery sources uncropped precisely so this is
   * safe — there is no paired `height`/`crop` to keep in step. Any that slip
   * in are dropped rather than scaled incorrectly.
   */
  function buildSrcset(src, maxWidth, widthList) {
    var url;
    try {
      url = new URL(src, window.location.href);
    } catch (error) {
      return '';
    }

    var params = url.searchParams;
    if (!params.has('width')) return '';
    params.delete('height');
    params.delete('crop');

    /* Never above the source's own width (no upscaling), and never above the
       largest width the layout can use — a 96px thumbnail asking for a
       1200w candidate is exactly what `sizes` is meant to prevent. */
    var cap = maxWidth || parseInt(params.get('width'), 10) || 0;
    cap = Math.min(cap, widthList[widthList.length - 1]);
    if (!cap) return '';

    var steps = widthList.filter(function (width) {
      return width < cap;
    });
    steps.push(cap);

    return steps
      .map(function (width) {
        params.set('width', String(width));
        return url.href + ' ' + width + 'w';
      })
      .join(', ');
  }

  function normalizeMedia(item) {
    if (!item || !item.src) return null;
    var width = parseInt(item.width, 10) || 1600;
    var height = parseInt(item.height, 10) || Math.round((width * 5) / 4);
    return {
      src: String(item.src),
      alt: item.alt == null ? '' : String(item.alt),
      width: width,
      height: height,
    };
  }

  /**
   * Attributes only — never the reflected IDL properties. `img.loading = ...`
   * is a no-op anywhere the property is unimplemented, which silently leaves
   * a swapped-in hero image lazy.
   *
   * Order matters: loading/fetchpriority/sizes/srcset are all set before
   * `src`, so the fetch the browser kicks off already has the right priority
   * and the right candidate list.
   */
  function applyImage(img, media, options) {
    if (!img) return;

    img.setAttribute('loading', options.eager ? 'eager' : 'lazy');
    if (options.eager) {
      img.setAttribute('fetchpriority', 'high');
    } else {
      img.removeAttribute('fetchpriority');
    }
    img.setAttribute('sizes', options.sizes);
    img.setAttribute('srcset', buildSrcset(media.src, media.width, options.widths));
    img.setAttribute('src', media.src);
    img.setAttribute('alt', options.alt == null ? media.alt : options.alt);
    img.setAttribute('width', String(options.width || media.width));
    img.setAttribute('height', String(options.height || media.height));
  }

  /**
   * One gallery arrow's two independent states.
   *
   * `present` — is there more than one image to move between. Expressed as
   *   `hidden`, which is only legitimate because the arrows are out of flow.
   * `clamped` — are we already at that end. Expressed as `aria-disabled`
   *   rather than `disabled`, so the button keeps its place in the tab order
   *   instead of dumping focus on <body> at the end of the gallery. The click
   *   and keyboard paths both no-op on a clamped arrow (`step()` returns
   *   early), so the attribute is a label for a refusal that already exists,
   *   not the mechanism.
   */
  function setArrowState(button, present, clamped) {
    if (!button) return;
    button.toggleAttribute('hidden', !present);
    button.setAttribute('aria-disabled', clamped ? 'true' : 'false');
  }

  class KoshaGallery extends HTMLElement {
    constructor() {
      super();
      this.media = [];
      this.index = 0;
      /* Where an arrow press is *heading*, while the smooth scroll is still in
         flight. Without it a second press before the first settles reads a
         stale `index` and lands on the slide you are already going to. */
      this.pendingIndex = null;
      this.pendingTimer = null;
      this.lightIndex = 0;
      this.lightPendingIndex = null;
      this.lightPendingTimer = null;
      this.lightboxBuilt = false;
      this.observer = null;
      this.lightObserver = null;
      /* Set while the expanded shopping view has the page's buy controls. */
      this.buyNodes = null;
      this.buyParent = null;
      this.buyAnchor = null;
      this.buyScrollY = 0;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleLightboxKeydown = this.handleLightboxKeydown.bind(this);
      this.handleLightboxClose = this.handleLightboxClose.bind(this);
      this.handleCartAdded = this.handleCartAdded.bind(this);
      this.handleDotFocus = this.handleDotFocus.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.dots = this.querySelector('[data-gallery-dots]');
      this.dotsTrack = this.querySelector('[data-gallery-dots-track]') || this.dots;
      /* The first dot of the visible window. See `windowDots()`. */
      this.dotStart = 0;
      this.thumbs = this.querySelector('[data-gallery-thumbs]');
      this.prevArrow = this.querySelector('[data-gallery-prev]');
      this.nextArrow = this.querySelector('[data-gallery-next]');
      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      this.lightboxTrack = this.querySelector('[data-gallery-lightbox-track]');
      this.lightThumbs = this.querySelector('[data-gallery-lightbox-thumbs]');
      this.lightboxBuy = this.querySelector('[data-gallery-lightbox-buy]');
      this.lightPrev = this.querySelector('[data-gallery-lightbox-prev]');
      this.lightNext = this.querySelector('[data-gallery-lightbox-next]');
      this.templates = this.querySelector('[data-gallery-templates]');
      this.mediaScript = this.querySelector('[data-gallery-media]');

      if (!this.track) return;

      if (this.media.length === 0) this.media = this.readMediaPayload();

      /* Same bound references every time, so a re-insertion cannot stack up
         duplicate listeners. */
      this.addEventListener('click', this.handleClick);
      this.track.addEventListener('keydown', this.handleKeydown);
      if (this.dots) this.dots.addEventListener('focusin', this.handleDotFocus);
      if (this.lightbox) {
        this.lightbox.addEventListener('click', this.handleDialogClick);
        this.lightbox.addEventListener('keydown', this.handleLightboxKeydown);
        this.lightbox.addEventListener('close', this.handleLightboxClose);

        /**
         * The `close` event is the semantically right hook, and it is not
         * reliable enough to be the only one.
         *
         * Measured in Chrome 15x against this page: the first Escape-driven
         * dismissal in a session fires `cancel` then `close`; every subsequent
         * one in the same browser session fires `cancel`, drops the `open`
         * attribute — and never fires `close`. The buy controls would then be
         * stranded inside a `display: none` dialog, which is to say the
         * product page would have no add-to-cart button.
         *
         * Watching the attribute itself cannot miss a close path: `close()`,
         * Escape, the backdrop, the close button and anything a future
         * component does all go through removing `open`. `unmountBuyColumn()`
         * is idempotent, so the two mechanisms overlapping costs nothing.
         */
        if (typeof MutationObserver === 'function') {
          var element = this;
          this.dialogObserver = new MutationObserver(function () {
            if (!element.lightbox.open) element.unmountBuyColumn();
          });
          this.dialogObserver.observe(this.lightbox, {
            attributes: true,
            attributeFilter: ['open'],
          });
        }
      }
      document.addEventListener('kosha:cart:added', this.handleCartAdded);

      this.observeSlides();
      this.syncArrows();
      this.syncLightState();
    }

    disconnectedCallback() {
      /* Never leave the page's only add-to-cart button inside a detached
         gallery. */
      this.unmountBuyColumn();
      document.removeEventListener('kosha:cart:added', this.handleCartAdded);
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.lightObserver) {
        this.lightObserver.disconnect();
        this.lightObserver = null;
      }
      if (this.dialogObserver) {
        this.dialogObserver.disconnect();
        this.dialogObserver = null;
      }
      if (this.pendingTimer) clearTimeout(this.pendingTimer);
      if (this.lightPendingTimer) clearTimeout(this.lightPendingTimer);
    }

    /* ---------------------------------------------------------------- data */

    readMediaPayload() {
      if (!this.mediaScript) return [];
      try {
        var parsed = JSON.parse(this.mediaScript.textContent);
        if (!Array.isArray(parsed)) return [];
        return parsed.map(normalizeMedia).filter(Boolean);
      } catch (error) {
        return [];
      }
    }

    get slides() {
      return Array.prototype.slice.call(
        this.track ? this.track.querySelectorAll('[data-gallery-slide]') : []
      );
    }

    template(selector) {
      if (!this.templates) return null;
      var node = this.templates.content.querySelector(selector);
      return node ? node.cloneNode(true) : null;
    }

    /* ------------------------------------------------------------ public API */

    /**
     * Replace every image in the gallery.
     * @param {Array<{src: string, alt: string, width: number, height: number}>} mediaArray
     * @returns {boolean} true when the gallery was rebuilt
     */
    setMedia(mediaArray) {
      if (!Array.isArray(mediaArray)) return false;

      var next = mediaArray.map(normalizeMedia).filter(Boolean);
      if (next.length === 0) return false;
      if (!this.track || !this.templates) return false;

      this.media = next;
      this.renderSlides();
      this.renderDots();
      this.renderThumbs();

      /* Lightbox contents are rebuilt lazily on the next open. */
      this.lightboxBuilt = false;
      this.lightIndex = 0;
      this.lightPendingIndex = null;
      if (this.lightPendingTimer) {
        clearTimeout(this.lightPendingTimer);
        this.lightPendingTimer = null;
      }
      if (this.lightObserver) {
        this.lightObserver.disconnect();
        this.lightObserver = null;
      }
      if (this.lightboxTrack) this.lightboxTrack.replaceChildren();

      this.setAttribute('data-count', String(next.length));
      if (this.mediaScript) this.mediaScript.textContent = JSON.stringify(next);

      this.observeSlides();
      this.clearPending();
      this.goTo(0, 'auto');
      this.setActive(0);

      /* A colour swap made from inside the expanded shopping view must not
         leave the shopper staring at an empty pane: rebuild the lightbox now
         rather than on the next open. Outside that case the lazy rebuild is
         still what happens — `lightbox.open` is false. */
      if (this.lightbox && this.lightbox.open) {
        this.buildLightbox();
        var element = this;
        requestAnimationFrame(function () {
          element.lightGoTo(0, 'auto');
          element.observeLightbox();
        });
      }
      this.syncLightState();
      return true;
    }

    /* ------------------------------------------------------------- rendering */

    renderSlides() {
      var element = this;
      var nodes = this.media.map(function (media, i) {
        var slide = element.template('[data-gallery-slide]');
        if (!slide) return null;
        applyImage(slide.querySelector('img'), media, {
          widths: SRCSET_WIDTHS,
          sizes: '(min-width: 990px) 48vw, 100vw',
          eager: i === 0,
        });
        var button = slide.querySelector('[data-gallery-open]');
        if (button) {
          button.setAttribute(
            'aria-label',
            'Open image ' + (i + 1) + ' of ' + element.media.length + ' full screen'
          );
        }
        return slide;
      });
      this.track.replaceChildren.apply(this.track, nodes.filter(Boolean));
    }

    renderDots() {
      if (!this.dots || !this.dotsTrack) return;
      var element = this;
      var total = this.media.length;
      var nodes = this.media.map(function (media, i) {
        var dot = element.template('[data-gallery-dot]');
        if (!dot) return null;
        /* The label carries the true position whatever the window is showing,
           which is the whole reason the dots are not thrown away and rebuilt
           seven at a time. */
        dot.setAttribute('aria-label', 'Go to image ' + (i + 1) + ' of ' + total);
        if (i === 0) dot.setAttribute('aria-current', 'true');
        return dot;
      });
      this.dotsTrack.replaceChildren.apply(this.dotsTrack, nodes.filter(Boolean));
      this.setSparse(this.dots);
      /* A swap can go from fifteen dots to one; the window has to start over
         or it would be scrolled past the end of a shorter row. */
      this.dotStart = 0;
      this.windowDots(0);
    }

    /**
     * WINDOWED DOTS — the iOS home-screen pattern.
     *
     * A fifteen-image colourway drew fifteen dots and ran them into both
     * viewport edges, which is what the owner saw on her phone: "the dots go
     * to the edge too much... just have less of them shown at a time".
     *
     * Every dot stays in the DOM — the accessible list is the true one, and a
     * screen reader still hears "Go to image 9 of 15" — and what changes is
     * only what is on screen. The frame is at most DOT_WINDOW dots wide and
     * the track slides inside it, so the row's width is a constant and its
     * side margins never shrink below the frame's own inset. Under the window
     * size nothing happens at all: the track is narrower than the frame, the
     * frame is `width: fit-content`, and the whole row centres itself.
     *
     * THE WINDOW MOVES ONLY WHEN THE ACTIVE DOT WOULD LEAVE IT, one slide at a
     * time, which is what keeps a swipe feeling like a swipe rather than like
     * the row scrolling underneath the thumb. A jump (a dot tap, an arrow, a
     * colour swap) clamps the same way, so the active dot is always inside.
     *
     * The offset is read from the track's own children rather than computed
     * from a dot width and a gap: the row is laid out by flex and the gap is a
     * token, so measuring is the only way this cannot drift from the CSS.
     *
     * @param {number} i the active slide
     */
    windowDots(i) {
      if (!this.dots || !this.dotsTrack) return;
      var dots = this.dotsTrack.children;
      var total = dots.length;
      var start = 0;

      if (total > DOT_WINDOW) {
        start = this.dotStart;
        if (i < start) start = i;
        else if (i > start + DOT_WINDOW - 1) start = i - DOT_WINDOW + 1;
        start = Math.max(0, Math.min(start, total - DOT_WINDOW));
      }
      this.dotStart = start;

      var anchor = dots[start];
      var offset = anchor ? anchor.offsetLeft : 0;
      this.dotsTrack.style.transform = offset ? 'translateX(' + -offset + 'px)' : '';

      /* The two dots at the ends of the window shrink when there is more of the
         row past them — the only affordance that says "there are more images"
         without adding a number to the page. The active dot never shrinks; it
         is the one thing in the row that has to stay readable. */
      var last = total > DOT_WINDOW ? start + DOT_WINDOW - 1 : total - 1;
      for (var d = 0; d < total; d += 1) {
        var edge = '';
        if (total > DOT_WINDOW && d === start && start > 0) edge = 'start';
        else if (total > DOT_WINDOW && d === last && last < total - 1) edge = 'end';
        if (edge) dots[d].setAttribute('data-dot-edge', edge);
        else dots[d].removeAttribute('data-dot-edge');
      }
    }

    /**
     * A clipped dot is still focusable, because it is still a real button in
     * the accessible list. Tabbing to one has to bring it into the window, or
     * the browser would try to scroll the frame instead and fight the
     * transform.
     */
    handleDotFocus(event) {
      if (!this.dotsTrack) return;
      var dot = event.target.closest ? event.target.closest('[data-gallery-dot]') : null;
      if (!dot) return;
      this.windowDots(Array.prototype.indexOf.call(this.dotsTrack.children, dot));
    }

    /**
     * A colour with a single photo has no useful dots or thumbnails. Normally
     * we collapse that chrome outright — but on a page whose colours swap in
     * place, collapsing it changes the media column's height and drags
     * everything below it (the whole info column, on mobile) up the screen.
     * There, keep the box and hide only the contents.
     *
     * `hidden` cannot be softened in CSS: base.css declares
     * `[hidden] { display: none !important }`. So the attribute itself has to
     * stay off, and `data-sparse` carries the state instead.
     */
    setSparse(node) {
      var sparse = this.media.length < 2;
      node.toggleAttribute('data-sparse', sparse);
      node.toggleAttribute('hidden', sparse && !document.querySelector('kosha-siblings'));
    }

    renderThumbs() {
      if (!this.thumbs) return;
      var element = this;
      var nodes = this.media.map(function (media, i) {
        var item = element.template('.gallery__thumb-item');
        if (!item) return null;
        applyImage(item.querySelector('img'), media, {
          widths: THUMB_WIDTHS,
          sizes: '96px',
          alt: '',
          width: 200,
          height: 250,
        });
        var button = item.querySelector('[data-gallery-thumb]');
        if (button) {
          button.setAttribute('aria-label', 'Show image ' + (i + 1));
          if (i === 0) button.setAttribute('aria-current', 'true');
        }
        return item;
      });
      this.thumbs.replaceChildren.apply(this.thumbs, nodes.filter(Boolean));
      this.setSparse(this.thumbs);
    }

    buildLightbox() {
      if (this.lightboxBuilt || !this.lightboxTrack) return;
      var element = this;
      var nodes = this.media.map(function (media, i) {
        var slide = element.template('[data-gallery-lightbox-slide]');
        if (!slide) return null;
        applyImage(slide.querySelector('img'), media, {
          widths: SRCSET_WIDTHS,
          sizes: '100vw',
          eager: i === 0,
        });
        return slide;
      });
      this.lightboxTrack.replaceChildren.apply(this.lightboxTrack, nodes.filter(Boolean));
      this.buildLightboxThumbs();
      this.lightboxBuilt = true;
      this.syncLightState();
    }

    /**
     * The expanded view's thumbnail strip. Cloned from the *same* template the
     * inline rail uses, so the two cannot drift; the only difference is which
     * container they land in, which is also how `handleClick` tells a strip
     * thumb from a rail thumb.
     */
    buildLightboxThumbs() {
      if (!this.lightThumbs) return;
      var element = this;
      var nodes = this.media.map(function (media, i) {
        var item = element.template('.gallery__thumb-item');
        if (!item) return null;
        applyImage(item.querySelector('img'), media, {
          widths: THUMB_WIDTHS,
          sizes: '96px',
          alt: '',
          width: 200,
          height: 250,
        });
        var button = item.querySelector('[data-gallery-thumb]');
        if (button) {
          button.setAttribute('aria-label', 'Show image ' + (i + 1));
          if (i === 0) button.setAttribute('aria-current', 'true');
        }
        return item;
      });
      this.lightThumbs.replaceChildren.apply(this.lightThumbs, nodes.filter(Boolean));
    }

    /* ------------------------------------------------------------- behaviour */

    observeSlides() {
      if (this.observer) this.observer.disconnect();
      if (!('IntersectionObserver' in window)) return;

      var element = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.55) return;
            var i = element.slides.indexOf(entry.target);
            if (i > -1) element.setActive(i);
          });
        },
        { root: this.track, threshold: [0.55, 0.9] }
      );

      this.slides.forEach(function (slide) {
        element.observer.observe(slide);
      });
    }

    setActive(i) {
      this.index = i;
      /* The scroll we asked for has arrived; stop projecting. */
      if (this.pendingIndex === i) this.clearPending();
      this.windowDots(i);
      this.syncCurrent(this.dotsTrack, '[data-gallery-dot]', i);
      this.syncCurrent(this.thumbs, '[data-gallery-thumb]', i);
      this.keepThumbVisible(this.thumbs ? this.thumbs.querySelectorAll('[data-gallery-thumb]')[i] : null);
      this.syncArrows();
    }

    /* ---------------------------------------------------------- arrows */

    /** The slide the next arrow press should count from. */
    effectiveIndex() {
      return this.pendingIndex == null ? this.index : this.pendingIndex;
    }

    clearPending() {
      this.pendingIndex = null;
      if (this.pendingTimer) {
        clearTimeout(this.pendingTimer);
        this.pendingTimer = null;
      }
    }

    /**
     * Move one slide. Clamped, never looped — see the `aria-disabled` note in
     * product.css §3.
     *
     * The projection is released either by the IntersectionObserver reporting
     * the slide we asked for, or by this timeout. The timeout is the safety
     * net for a scroll that never completes (a display:none track, a browser
     * that ignores the request); without it a stuck `pendingIndex` would make
     * every later press count from a slide the shopper is not on.
     */
    step(delta) {
      var count = this.slides.length;
      if (count < 2) return;
      var next = this.effectiveIndex() + delta;
      if (next < 0 || next > count - 1) return;

      this.pendingIndex = next;
      this.syncArrows();
      this.goTo(next);

      var element = this;
      if (this.pendingTimer) clearTimeout(this.pendingTimer);
      this.pendingTimer = setTimeout(function () {
        element.pendingTimer = null;
        element.pendingIndex = null;
        element.syncArrows();
      }, 700);
    }

    /**
     * `hidden` is safe on these two and only these two: they are
     * `position: absolute`, so removing them cannot change the media column's
     * height. Everything else in this component that disappears on a
     * one-image colour uses `data-sparse` and keeps its box (see setSparse).
     */
    syncArrows() {
      var count = this.slides.length;
      var i = this.effectiveIndex();
      setArrowState(this.prevArrow, count > 1, i <= 0);
      setArrowState(this.nextArrow, count > 1, i >= count - 1);
    }

    /**
     * Keep the active thumbnail inside the thumbnail rail.
     *
     * This deliberately does NOT use `scrollIntoView`. That API scrolls every
     * scrollable ancestor, and the rail is a wrapping flex row with
     * `overflow: visible` — it is not a scroll container at all, so the only
     * box `scrollIntoView` could ever move here is the *document*. Combined
     * with the theme's global `html { scroll-behavior: smooth }`, the old call
     * smoothly scrolled the whole page by the header's height every time
     * `setActive` ran: once on load, and once per sibling colour swap. That
     * was the "changing colour scrolls the page" defect.
     *
     * Assigning `scrollTop`/`scrollLeft` is instant (immune to
     * `scroll-behavior`) and can only ever move this one element.
     */
    keepThumbVisible(thumb) {
      var rail = this.thumbs;
      if (!rail || !thumb) return;

      var canScrollY = rail.scrollHeight > rail.clientHeight;
      var canScrollX = rail.scrollWidth > rail.clientWidth;
      if (!canScrollY && !canScrollX) return;

      var item = thumb.getBoundingClientRect();
      var box = rail.getBoundingClientRect();

      if (canScrollY) {
        if (item.top < box.top) rail.scrollTop -= box.top - item.top;
        else if (item.bottom > box.bottom) rail.scrollTop += item.bottom - box.bottom;
      }
      if (canScrollX) {
        if (item.left < box.left) rail.scrollLeft -= box.left - item.left;
        else if (item.right > box.right) rail.scrollLeft += item.right - box.right;
      }
    }

    syncCurrent(container, selector, i) {
      if (!container) return;
      container.querySelectorAll(selector).forEach(function (node, nodeIndex) {
        if (nodeIndex === i) {
          node.setAttribute('aria-current', 'true');
        } else {
          node.removeAttribute('aria-current');
        }
      });
    }

    goTo(i, behavior) {
      var slides = this.slides;
      if (slides.length === 0) return;
      var target = slides[Math.max(0, Math.min(i, slides.length - 1))];
      this.track.scrollTo({
        left: target.offsetLeft - this.track.offsetLeft,
        behavior: behavior || (prefersReducedMotion() ? 'auto' : 'smooth'),
      });
    }

    /* -------------------------------------------------------- lightbox nav */

    get lightSlides() {
      return Array.prototype.slice.call(
        this.lightboxTrack ? this.lightboxTrack.children : []
      );
    }

    /**
     * Deliberately assigns `scrollTop` instead of calling `scrollIntoView` on
     * the figure — the exact swap this component already made once, in
     * `keepThumbVisible()`. `scrollIntoView` walks up and scrolls *every*
     * scrollable ancestor; the lightbox is a top-layer `<dialog>`, whose
     * containing block is the viewport, so the document is on that list. With
     * `html { scroll-behavior: smooth }` in base.css that produces a silent
     * page scroll underneath the overlay, which the shopper then discovers on
     * close. Assigning the scroll offset of the one element that actually
     * scrolls cannot do that.
     */
    lightGoTo(i, behavior) {
      var slides = this.lightSlides;
      if (slides.length === 0 || !this.lightboxTrack) return;
      var next = Math.max(0, Math.min(i, slides.length - 1));
      var target = slides[next];
      this.lightIndex = next;
      this.lightboxTrack.scrollTo({
        /* Slides are not positioned, so their offsetParent is the dialog, not
           the track — hence the subtraction. Same arithmetic `goTo()` uses on
           the inline track. */
        top: target.offsetTop - this.lightboxTrack.offsetTop,
        left: 0,
        behavior: behavior || (prefersReducedMotion() ? 'auto' : 'smooth'),
      });
      this.syncLightState();
    }

    /** Same projection-while-scrolling story as `step()`. */
    lightSeek(next) {
      var count = this.lightSlides.length;
      if (count < 2) return;
      if (next < 0 || next > count - 1) return;

      this.lightPendingIndex = next;
      this.lightGoTo(next);

      var element = this;
      if (this.lightPendingTimer) clearTimeout(this.lightPendingTimer);
      this.lightPendingTimer = setTimeout(function () {
        element.lightPendingTimer = null;
        element.lightPendingIndex = null;
        element.syncLightState();
      }, 700);
    }

    lightStep(delta) {
      var base = this.lightPendingIndex == null ? this.lightIndex : this.lightPendingIndex;
      this.lightSeek(base + delta);
    }

    syncLightState() {
      /* Before the first open the track is empty by design (12 full-size
         images stay out of the initial HTML), so the count comes from the
         media list rather than from the DOM. */
      var count = this.lightboxBuilt ? this.lightSlides.length : this.media.length;
      var i = this.lightPendingIndex == null ? this.lightIndex : this.lightPendingIndex;
      setArrowState(this.lightPrev, count > 1, i <= 0);
      setArrowState(this.lightNext, count > 1, i >= count - 1);
      this.syncCurrent(this.lightThumbs, '[data-gallery-thumb]', this.lightIndex);
      this.keepStripVisible();
    }

    /** Same contract as `keepThumbVisible()`, and for the same reason: assign
     *  the strip's own scroll offset, never `scrollIntoView`. */
    keepStripVisible() {
      var rail = this.lightThumbs;
      if (!rail) return;
      var thumb = rail.querySelectorAll('[data-gallery-thumb]')[this.lightIndex];
      if (!thumb || rail.scrollWidth <= rail.clientWidth) return;
      var item = thumb.getBoundingClientRect();
      var box = rail.getBoundingClientRect();
      if (item.left < box.left) rail.scrollLeft -= box.left - item.left;
      else if (item.right > box.right) rail.scrollLeft += item.right - box.right;
    }

    /**
     * Registered *after* the opening scroll, not at build time: the first
     * callback reports whatever was on screen when `observe()` ran, and at
     * build time that is always slide 0 — which would tell a lightbox opened
     * on slide 5 that its "previous" arrow is at the start of the gallery.
     */
    observeLightbox() {
      if (this.lightObserver) this.lightObserver.disconnect();
      if (!('IntersectionObserver' in window) || !this.lightboxTrack) return;

      var element = this;
      this.lightObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.55) return;
            var i = element.lightSlides.indexOf(entry.target);
            if (i < 0) return;
            if (element.lightPendingIndex != null && i !== element.lightPendingIndex) return;
            if (element.lightPendingIndex === i) {
              clearTimeout(element.lightPendingTimer);
              element.lightPendingTimer = null;
              element.lightPendingIndex = null;
            }
            element.lightIndex = i;
            element.syncLightState();
          });
        },
        { root: this.lightboxTrack, threshold: [0.55, 0.9] }
      );

      this.lightSlides.forEach(function (slide) {
        element.lightObserver.observe(slide);
      });
    }

    /* ------------------------------------------ expanded shopping view */

    /**
     * Relocate the page's real buy controls into the lightbox.
     *
     * Relocated, not mirrored. `<kosha-product-form>` owns variant state; a
     * second copy of the size pills would mean two DOM trees each believing
     * they are the selection, and every bug that follows from that. Moving the
     * originals means a size chosen in here *is* the page's size when the view
     * closes, with no synchronisation code at all. It is the same move
     * `hoistSizeGuide()` makes for the same class of reason.
     *
     * Both custom elements involved get `connectedCallback` again on the move.
     * `<kosha-product-form>` is idempotent there by construction (it re-reads
     * its own `[data-variant-map]` script and re-binds with the same bound
     * references, which `addEventListener` de-duplicates).
     * `<kosha-siblings>` is not — it would recapture its "landing colour"
     * baselines from whatever colour is on screen — so it carries an explicit
     * re-entry guard.
     */
    mountBuyColumn() {
      if (!this.lightboxBuy || this.buyNodes) return;
      if (typeof window.matchMedia !== 'function') return;
      if (!window.matchMedia('(min-width: 990px)').matches) return;

      var info = document.querySelector('[data-product-info]');
      if (!info) return;

      var nodes = [];
      ['.product__title', '.product__price-row', '[data-slot="swatches"]', 'kosha-product-form']
        .forEach(function (selector) {
          var node = info.querySelector(selector);
          if (node) nodes.push(node);
        });
      if (nodes.length === 0) return;

      /* They are consecutive children of the info column, so one anchor puts
         all of them back in the original order. */
      this.buyParent = nodes[0].parentNode;
      this.buyAnchor = nodes[nodes.length - 1].nextSibling;
      this.buyScrollY = window.scrollY;
      this.buyNodes = nodes;

      var host = this.lightboxBuy;
      nodes.forEach(function (node) {
        host.appendChild(node);
      });
      this.lightbox.setAttribute('data-expanded', '');
    }

    unmountBuyColumn() {
      if (!this.buyNodes) return;
      var parent = this.buyParent;
      var anchor = this.buyAnchor;
      this.buyNodes.forEach(function (node) {
        parent.insertBefore(node, anchor);
      });
      this.buyNodes = null;
      this.buyParent = null;
      this.buyAnchor = null;
      if (this.lightbox) this.lightbox.removeAttribute('data-expanded');

      /* The info column loses ~400px of content while the view is open and
         gets it back here. If the document became short enough for the browser
         to clamp the scroll offset in between, closing would otherwise dump
         the shopper somewhere they never scrolled to. Same guard, same
         `behavior: 'auto'` reasoning, as `<kosha-siblings>.holdScroll()`. */
      var y = this.buyScrollY;
      var restore = function () {
        if (Math.abs(window.scrollY - y) < 1) return;
        window.scrollTo({ top: y, left: window.scrollX, behavior: 'auto' });
      };
      restore();
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(restore);
    }

    handleLightboxClose() {
      this.unmountBuyColumn();
    }

    /**
     * The cart drawer is a plain `position: fixed` panel at z-index 90, and a
     * modal `<dialog>` is in the top layer above every z-index there is — so
     * an add from inside the expanded view would open the cart *underneath* it.
     * Closing first is the same "design the overlap out" call the size-guide
     * drawer makes on this exact event, and it puts the buy controls back
     * before anything else can look for them.
     */
    handleCartAdded() {
      if (this.lightbox && this.lightbox.open) this.lightbox.close();
    }

    openLightbox(i) {
      if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;
      this.buildLightbox();
      this.mountBuyColumn();
      this.lightbox.showModal();
      var element = this;
      /* One frame, so the dialog has been laid out and `offsetTop` is real. */
      requestAnimationFrame(function () {
        element.lightPendingIndex = null;
        element.lightGoTo(i, 'auto');
        element.observeLightbox();
      });
    }

    handleClick(event) {
      var prev = event.target.closest('[data-gallery-prev]');
      if (prev) {
        this.step(-1);
        return;
      }

      var next = event.target.closest('[data-gallery-next]');
      if (next) {
        this.step(1);
        return;
      }

      var lightPrev = event.target.closest('[data-gallery-lightbox-prev]');
      if (lightPrev) {
        this.lightStep(-1);
        return;
      }

      var lightNext = event.target.closest('[data-gallery-lightbox-next]');
      if (lightNext) {
        this.lightStep(1);
        return;
      }

      var open = event.target.closest('[data-gallery-open]');
      if (open) {
        event.preventDefault();
        var slide = open.closest('[data-gallery-slide]');
        this.openLightbox(this.slides.indexOf(slide));
        return;
      }

      var close = event.target.closest('[data-gallery-close]');
      if (close && this.lightbox) {
        this.lightbox.close();
        return;
      }

      var dot = event.target.closest('[data-gallery-dot]');
      if (dot && this.dotsTrack) {
        this.goTo(Array.prototype.indexOf.call(this.dotsTrack.children, dot));
        return;
      }

      var thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb) {
        var item = thumb.closest('.gallery__thumb-item');
        /* The strip and the rail are clones of one template, so the container
           is what tells them apart — not a second marker attribute that could
           get out of step with the template. */
        if (this.lightThumbs && this.lightThumbs.contains(thumb)) {
          this.lightSeek(Array.prototype.indexOf.call(this.lightThumbs.children, item));
        } else if (this.thumbs) {
          this.goTo(Array.prototype.indexOf.call(this.thumbs.children, item));
        }
      }
    }

    handleKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
      }
    }

    /**
     * Left/right and up/down both work, and for different reasons: left/right
     * because that is what the two visible controls say, up/down because that
     * is genuinely which way this track scrolls. Escape stays the dialog's own.
     */
    handleLightboxKeydown(event) {
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.lightStep(1);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.lightStep(-1);
      }
    }

    handleDialogClick(event) {
      /* A click that lands on the dialog element itself is a backdrop click:
         every real target is inside the track or the close button. */
      if (event.target === this.lightbox) this.lightbox.close();
    }
  }

  if (!customElements.get('kosha-gallery')) {
    customElements.define('kosha-gallery', KoshaGallery);
  }

  /* ==========================================================================
     SHARED CART HELPERS  →  window.kosha

     Full copies used to live here, under a note saying Phase 2's drawer should
     lift them into global.js. That happened. Named locals rather than
     `window.kosha.x` at ~40 call sites, resolved at call time so nothing here
     depends on evaluation order. `seedCartCount` is gone entirely: global.js
     runs the identical seed on every page.
     ========================================================================== */

  /* ==========================================================================
     PRE-ORDERS

     Owner, 2026-09-05: "when I flip between colors the add to cart button
     disappears, half the time the pre-order button doesn't show up, sometimes
     it shows just as in stock, it's a total disaster".

     All three were the same mistake, made twice: asking the pre-order APP,
     in the browser, which variants are pre-orders.

     The app answers by watching a hidden input's `value` ATTRIBUTE for the
     variant on screen. This theme changes colour with no navigation and sets
     that input's `value` PROPERTY, which changes no attribute, so the app went
     on describing the colour the shopper had left. It also only latches onto
     the page at all if that input already holds a real variant id at first
     paint, which is true only when the URL carries `?variant=` — so on one
     visit it worked and on the next it did nothing. And when it does latch on,
     it hides this theme's buy buttons (`position: fixed; left: -9999px`) so it
     can substitute its own; a colour swap it cannot see leaves them hidden
     with nothing put back. That is the missing ADD TO CART.

     None of that is fixable from inside the app, and none of it needs to be.
     Shopify already knows everything the decision needs, and Liquid can see all
     of it at once on the server, before a single script runs:

       covered by a pre-order plan   the selling plan group's `app_id`
       nothing on the shelf          `inventory_quantity` (the app's campaigns
                                     are all "when out of stock", and it sets
                                     the variant to keep selling, which is why
                                     `available` says true on a garment that
                                     does not exist yet)
       what was promised             the selling plan's own name, which is the
                                     merchant's "Ships on or before October 1"

     snippets/preorder-index.liquid renders that as one object covering every
     variant this page can reach — this product, every colour of it, the set
     partner and every colour of that, and the Complete the set row. Everything
     below reads it and nothing polls, races or re-fetches. It is right at first
     paint, it survives a colour swap because the swapped-in colour is already
     in it, and it works in the local preview, where the app is not installed.

     The app's embed is still read, but only for two strings it owns and Shopify
     does not: the button's wording and the cart note's. Both have theme
     defaults, so a page without the app says the same thing.
     ========================================================================== */

  /**
   * variant id → { message, plan, planRequired }, from the server render.
   *
   * Parsed once. A missing or malformed script is "no pre-orders on this page",
   * never a broken product page.
   */
  var preorderMap = null;
  function preorderIndex() {
    if (preorderMap) return preorderMap;
    preorderMap = {};
    var node = document.querySelector('[data-preorder-index]');
    if (!node) return preorderMap;
    try {
      var parsed = JSON.parse(node.textContent);
      Object.keys(parsed).forEach(function (key) {
        /* "_" is the sentinel the Liquid opens the object with; see the note
           in preorder-index.liquid. */
        if (key === '_') return;
        var entry = parsed[key];
        if (entry && typeof entry === 'object') preorderMap[key] = entry;
      });
    } catch (error) {
      /* Left empty. */
    }
    return preorderMap;
  }

  /**
   * The two strings the pre-order app owns.
   *
   * `Note: PRE-ORDER` under the garment's name in the cart drawer and again on
   * the checkout page is not a feature of either: it is an ordinary line item
   * property, and Shopify prints a line's visible properties in both places by
   * itself. So this pair IS the cart and checkout behaviour the owner asked to
   * match, and getting the two strings right is the whole job.
   *
   * Theme defaults come from Liquid so the cart reads correctly with the app
   * absent; the app's own campaign values win whenever its embed is present, so
   * editing the wording in the app still changes what the cart says.
   */
  var preorderCopyCache = null;
  function preorderCopy() {
    if (preorderCopyCache) return preorderCopyCache;

    var copy = { noteKey: 'Note', noteText: 'PRE-ORDER', buttonText: 'Pre-order' };
    var node = document.querySelector('[data-preorder-copy]');
    if (node) {
      try {
        var parsed = JSON.parse(node.textContent);
        if (parsed.noteKey) copy.noteKey = parsed.noteKey;
        if (parsed.noteText) copy.noteText = parsed.noteText;
        if (parsed.buttonText) copy.buttonText = parsed.buttonText;
      } catch (error) {
        /* Defaults stand. */
      }
    }

    /* The app's embed, when it is on the page. Every campaign on this store
       carries the same three values, so the first one that parses is taken
       rather than tracking them per variant: a second campaign wording would be
       a merchant decision nobody has made, and reading it per variant would put
       a second source of truth next to the index for no gain. */
    var embed = document.querySelector('essential-preorder-app-embed');
    var raw = embed && embed.getAttribute('preorder-app-data');
    if (raw) {
      try {
        var data = JSON.parse(raw);
        (data.preorderCampaigns || []).forEach(function (campaign) {
          if (copy.fromApp) return;
          var content = (campaign && campaign.content) || {};
          if (!content.cartNoteKey || !content.cartNoteText) return;
          copy.noteKey = content.cartNoteKey;
          copy.noteText = content.cartNoteText;
          if (content.buttonText) copy.buttonText = content.buttonText;
          copy.fromApp = true;
        });
      } catch (error) {
        /* Defaults stand. */
      }
    }

    preorderCopyCache = copy;
    return copy;
  }

  /**
   * The app's own hidden property, variant id → campaign id.
   *
   * The theme cannot invent one of these, so it is written only when the embed
   * is on the page to supply it, and nothing the shopper sees depends on it. It
   * is how the app later recognises one of its own cart lines, and it already
   * falls back to matching on the variant id when the property is missing.
   */
  var preorderCampaignMap = null;
  function preorderCampaignIds() {
    if (preorderCampaignMap) return preorderCampaignMap;
    preorderCampaignMap = {};
    var embed = document.querySelector('essential-preorder-app-embed');
    var raw = embed && embed.getAttribute('preorder-app-data');
    if (!raw) return preorderCampaignMap;
    try {
      var data = JSON.parse(raw);
      (data.preorderCampaigns || []).forEach(function (campaign) {
        var placement = (campaign && campaign.placement) || {};
        (placement.specificProducts || []).forEach(function (target) {
          var match = /ProductVariant\/(\d+)/.exec((target && target.id) || '');
          if (match && campaign.id) preorderCampaignMap[match[1]] = campaign.id;
        });
      });
    } catch (error) {
      /* Left empty. */
    }
    return preorderCampaignMap;
  }

  /** The index entry for a variant id, or null. */
  function preorderFor(id) {
    if (id == null) return null;
    return preorderIndex()[String(id)] || null;
  }

  /**
   * Is this variant a pre-order?
   *
   * One lookup, and deliberately no second condition. The server already
   * combined "covered by a campaign" with "nothing in stock" — trying to
   * re-check either here is what produced the old test's two wrong answers
   * (`available !== true` said no on every pre-order, because the app keeps
   * them sellable; the `oversell` flag it was replaced with was dropped by the
   * colour swap, which rebuilt the variant list from a Shopify endpoint that
   * carries no inventory).
   */
  function isPreorder(variant) {
    if (!variant) return false;
    return !!preorderFor(variant.id);
  }

  /**
   * The cart line a pre-order variant has to become.
   *
   *   properties[Note] = "PRE-ORDER"   visible, so Shopify prints it in the
   *                                    cart drawer and on the checkout page
   *   properties[_essential_preorder_campaign_id]   hidden, app bookkeeping,
   *                                    only when the app's embed supplies it
   *   selling_plan                     only when the plan actually changes the
   *                                    transaction, which on this store it
   *                                    never does — see preorder-entries.liquid
   *
   * Returns null when the variant is not a pre-order, which is also the signal
   * to take these keys back OFF a line that used to be one: an in-stock size
   * must never reach the checkout wearing the note left behind by the size
   * chosen before it.
   */
  function preorderLine(variant) {
    var entry = variant && preorderFor(variant.id);
    if (!entry) return null;

    var copy = preorderCopy();
    var properties = {};
    properties[copy.noteKey] = copy.noteText;

    var campaignId = preorderCampaignIds()[String(variant.id)];
    if (campaignId) properties._essential_preorder_campaign_id = campaignId;

    return {
      properties: properties,
      sellingPlan: entry.planRequired ? entry.plan || null : null,
    };
  }

  /**
   * Every property key and selling plan a pre-order line can carry, so one that
   * is no longer a pre-order can be stripped of them.
   */
  function preorderOwnedKeys() {
    var keys = { properties: {}, plans: {} };
    keys.properties._essential_preorder_campaign_id = true;
    keys.properties[preorderCopy().noteKey] = true;
    var index = preorderIndex();
    Object.keys(index).forEach(function (id) {
      if (index[id].plan) keys.plans[String(index[id].plan)] = true;
    });
    return keys;
  }

  /** Available to Shopify, or sellable as a pre-order. */
  function isBuyable(variant) {
    if (!variant) return false;
    return variant.available === true || !!preorderFor(variant.id);
  }

  function cartRoute(path) {
    return window.kosha.cartRoute(path);
  }

  /* Rewrites every [data-cart-count] the header rendered, data-empty included. */
  function updateCartCount(count) {
    return window.kosha.updateCartCount(count);
  }

  /* Integer cents from /products/<handle>.js; Liquid renders carry
     `price_formatted` already. */
  function formatMoney(cents, format) {
    return window.kosha.formatMoney(cents, format);
  }

  /* ==========================================================================
     <kosha-product-form>

     Owns variant state for the whole info column: size pills → variant id →
     price / button state / back-in-stock stub, plus the AJAX add.

     Public interface (hard contract for Task 6):

       form.setProduct(productJson)   // /products/<handle>.js shape → boolean

     Dispatches a bubbling, composed `kosha:cart:added` CustomEvent after a
     successful add. Phase 2's cart drawer listens for it.
     ========================================================================== */

  /** Normalise a /products/<handle>.js (or Liquid-rendered) variant entry. */
  function normalizeVariant(raw) {
    if (!raw || raw.id == null) return null;
    var options = raw.options;
    if (!Array.isArray(options)) {
      options = [raw.option1, raw.option2, raw.option3].filter(function (value) {
        return value != null;
      });
    }
    return {
      id: raw.id,
      title: raw.title == null ? '' : String(raw.title),
      options: options.map(String),
      available: raw.available !== false,
      /* NOTHING ABOUT PRE-ORDERS IS CARRIED HERE, and that is the fix rather
         than an omission. Two earlier versions tried to put the pre-order tell
         on the variant — first `available`, then an `oversell` flag Liquid
         stamped — and both were wiped by the colour swap, which rebuilds this
         list from a Shopify payload that knows nothing about either. The index
         is keyed by variant id and covers every colour on the page, so it
         answers for a swapped-in variant exactly as it does for a rendered
         one. See `preorderIndex()`. */
      price: Number(raw.price) || 0,
      compare_at_price: raw.compare_at_price == null ? null : Number(raw.compare_at_price),
      price_formatted: raw.price_formatted || null,
      compare_at_price_formatted: raw.compare_at_price_formatted || null,
    };
  }

  /**
   * Normalise option groups. /products/<handle>.js returns objects
   * ({name, position, values}); some Shopify payloads return bare name
   * strings, in which case values are derived from the variants.
   */
  function normalizeOptions(product, variants) {
    var raw = product.options;
    if (!Array.isArray(raw) || raw.length === 0) return [];

    if (typeof raw[0] === 'string') {
      return raw.map(function (name, i) {
        var seen = [];
        variants.forEach(function (variant) {
          var value = variant.options[i];
          if (value != null && seen.indexOf(value) === -1) seen.push(value);
        });
        return { name: name, position: i + 1, values: seen };
      });
    }

    return raw.map(function (option, i) {
      return {
        name: option.name == null ? '' : String(option.name),
        position: option.position || i + 1,
        values: (option.values || []).map(String),
      };
    });
  }

  /* "Kanti Crop - Azalea" → "Kanti Crop size"; "Size" → "Size". Mirrors the
     Liquid in size-selector.liquid. */
  function groupLegend(name) {
    var lower = String(name).toLowerCase();
    if (lower === 'size' || lower === 'title') return 'Size';
    return String(name).split(' - ')[0].trim() + ' size';
  }

  function isDefaultOnly(options) {
    return (
      options.length === 0 ||
      (options.length === 1 &&
        options[0].values.length === 1 &&
        options[0].values[0] === 'Default Title')
    );
  }

  class KoshaProductForm extends HTMLElement {
    constructor() {
      super();
      this.variants = [];
      this.currentVariant = null;
      this.resetTimer = null;
      this.checkoutTimer = null;
      this.walletTimer = null;
      this.walletObserver = null;
      this.pending = false;
      this.handleChange = this.handleChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleSiblingChanged = this.handleSiblingChanged.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.idInput = this.querySelector('[data-variant-id]');
      this.mapScript = this.querySelector('[data-variant-map]');
      this.selector = this.querySelector('[data-size-selector]');
      this.groupsHost = this.querySelector('[data-size-groups]');
      this.templates = this.querySelector('[data-size-templates]');
      this.button = this.querySelector('[data-atc]');
      this.buttonText = this.querySelector('[data-atc-text]');
      this.errorNode = this.querySelector('[data-form-error]');
      this.bis = this.querySelector('[data-bis]');
      this.preorderNote = this.querySelector('[data-preorder-note]');

      var info = this.closest('[data-product-info]') || document;
      this.priceNode = info.querySelector('[data-price]');
      this.titleNode = info.querySelector('.product__title');

      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
      this.variants = this.readVariantMap();

      this.addEventListener('change', this.handleChange);
      if (this.form) this.form.addEventListener('submit', this.handleSubmit);
      document.addEventListener('kosha:sibling:changed', this.handleSiblingChanged);
      /* On `window`, not on this element: a bfcache restore is a page event and
         never reaches a custom element as one. See `handlePageShow`. */
      window.addEventListener('pageshow', this.handlePageShow);
      this.watchWallet();

      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      if (this.form) this.form.removeEventListener('submit', this.handleSubmit);
      document.removeEventListener('kosha:sibling:changed', this.handleSiblingChanged);
      window.removeEventListener('pageshow', this.handlePageShow);
      if (this.resetTimer) clearTimeout(this.resetTimer);
      if (this.checkoutTimer) clearTimeout(this.checkoutTimer);
      if (this.walletTimer) clearTimeout(this.walletTimer);
      if (this.walletObserver) {
        this.walletObserver.disconnect();
        this.walletObserver = null;
      }
    }

    /* ---------------------------------------------------------------- data */

    readVariantMap() {
      if (!this.mapScript) return [];
      try {
        var parsed = JSON.parse(this.mapScript.textContent);
        if (!Array.isArray(parsed)) return [];
        return parsed.map(normalizeVariant).filter(Boolean);
      } catch (error) {
        return [];
      }
    }

    get groups() {
      return Array.prototype.slice.call(this.querySelectorAll('[data-size-group]'));
    }

    /** Selected value per group; `null` where the shopper has not chosen yet. */
    selection() {
      return this.groups.map(function (group) {
        var checked = group.querySelector('[data-size-input]:checked');
        return checked ? checked.value : null;
      });
    }

    selectionComplete() {
      return this.selection().every(function (value) {
        return value !== null;
      });
    }

    matchVariant(selection) {
      if (selection.length === 0) return this.variants[0] || null;
      var found = null;
      this.variants.forEach(function (variant) {
        if (found) return;
        var hit = selection.every(function (value, i) {
          return variant.options[i] === value;
        });
        if (hit) found = variant;
      });
      return found;
    }

    /* ----------------------------------------------------------- rendering */

    /**
     * Grey out values that cannot combine with what is already chosen in the
     * *other* groups. The server render only knows global availability; this
     * is the per-selection narrowing (choose "XS" top on Kanti and the "XS"
     * bottom goes struck, because XS/XS is sold out).
     */
    refreshAvailability() {
      var element = this;
      var selection = this.selection();

      this.groups.forEach(function (group, groupIndex) {
        group.querySelectorAll('[data-size-input]').forEach(function (input) {
          var reachable = element.variants.some(function (variant) {
            if (!isBuyable(variant)) return false;
            if (variant.options[groupIndex] !== input.value) return false;
            return selection.every(function (value, i) {
              return i === groupIndex || value === null || variant.options[i] === value;
            });
          });

          input.toggleAttribute('data-unavailable', !reachable);
          var label = input.closest('.pill');
          if (label) label.classList.toggle('pill--unavailable', !reachable);
        });

        var chosen = group.querySelector('[data-size-input]:checked');
        var readout = group.querySelector('[data-size-group-chosen]');
        if (readout) readout.textContent = chosen ? chosen.value : '';
      });
    }

    /**
     * SAY OUT LOUD WHICH SIZE IS CHOSEN.
     *
     * Until this existed, the page never told anything outside itself. The
     * chosen variant went into the hidden input as `input.value = id`, which
     * is a JavaScript PROPERTY assignment: it changes no attribute, fires no
     * event, and touches no URL. Nothing watching the page can see it happen.
     *
     * That is not a small omission, it is the single defect behind two
     * separate things the owner reported on the same day. The pre-order app
     * watches that input's `value` ATTRIBUTE, saw nothing, and went on
     * describing the previous variant while hiding the buy buttons it had
     * hidden for it. And Postscript's back-in-stock link, which is supposed to
     * appear on a sold-out size, is decided by an SDK reading the same page
     * and getting the same silence.
     *
     * A storefront app has exactly three ways to know a theme's selection
     * changed, so all three are now true at once:
     *
     *   the `value` ATTRIBUTE, for anything using a MutationObserver
     *   a bubbling `change` EVENT, for anything using a listener
     *   `?variant=` in the URL, for anything reading location
     *
     * This is the ordinary contract a Shopify theme is expected to honour;
     * this one simply never did. The three existing document-level `change`
     * listeners in this file all gate on `[data-size-input]`, so none of them
     * reacts to this input, and the early return in `handleChange` means the
     * event cannot loop back through `update()`.
     *
     * Guarded on an actual change, because `update()` runs on every render and
     * an app being told "the variant changed" when it did not is its own kind
     * of lie.
     */
    publishVariant(variant) {
      if (!this.idInput) return;
      var value = variant ? String(variant.id) : '';
      if (this.idInput.value === value && this.idInput.getAttribute('value') === value) {
        return;
      }

      this.idInput.value = value;
      this.idInput.setAttribute('value', value);
      this.syncVariantUrl(variant);

      if (typeof Event === 'function') {
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /**
     * Keep `?variant=` on the address bar in step with the pills.
     *
     * `replaceState`, never `pushState`: choosing a size is not a page the
     * back button should have to walk through. `history.state` is passed
     * straight back so `<kosha-siblings>`'s `{ koshaSibling }` marker survives,
     * and only the query string is touched, so `handlePopState` — which reads
     * `location.pathname` and nothing else — cannot be confused by it.
     *
     * Worth having on its own merits, quite apart from any app: a shopper who
     * refreshes, or sends the link to someone, keeps the size she picked.
     */
    syncVariantUrl(variant) {
      if (typeof history === 'undefined' || typeof history.replaceState !== 'function') {
        return;
      }
      try {
        var url = new URL(window.location.href);
        if (variant) url.searchParams.set('variant', String(variant.id));
        else url.searchParams.delete('variant');
        if (url.href === window.location.href) return;
        history.replaceState(history.state, '', url.href);
      } catch (error) {
        /* A URL this theme cannot parse is not worth failing a size change
           over. */
      }
    }

    setPrice(variant) {
      if (!this.priceNode || !variant) return;

      var onSale =
        variant.compare_at_price != null && variant.compare_at_price > variant.price;
      this.priceNode.classList.toggle('price--on-sale', onSale);

      var current = this.priceNode.querySelector('[data-price-current]');
      if (current) {
        current.textContent =
          variant.price_formatted || formatMoney(variant.price, this.moneyFormat);
      }

      var compare = this.priceNode.querySelector('[data-price-compare]');
      if (onSale) {
        if (!compare) {
          compare = document.createElement('s');
          compare.className = 'price__was';
          compare.setAttribute('data-price-compare', '');
          this.priceNode.appendChild(compare);
        }
        compare.hidden = false;
        compare.textContent =
          variant.compare_at_price_formatted ||
          formatMoney(variant.compare_at_price, this.moneyFormat);
      } else if (compare) {
        compare.hidden = true;
      }
    }

    setButton(state, text) {
      if (!this.button) return;
      this.button.setAttribute('data-state', state);
      this.button.toggleAttribute('aria-disabled', state === 'unavailable');
      if (this.buttonText) this.buttonText.textContent = text;

      var sticky = document.querySelector('[data-sticky-btn-text]');
      if (sticky) sticky.textContent = text;
      var stickyButton = document.querySelector('[data-sticky-btn]');
      if (stickyButton) stickyButton.toggleAttribute('aria-disabled', state === 'unavailable');
    }

    /* ------------------------------------------------------------- the app */

    /* THE BUY BUTTONS ARE NO LONGER DEFENDED FROM HERE, and the deletion is
       the fix.

       Essential Pre-order claims the add-to-cart button of whatever theme it
       lands in: it finds `form[method="post"][action*="/cart/add"]`, moves the
       theme's submit buttons off-screen (`position: fixed; left: -9999px;
       transform: scale(0)`) and renders its own in their place. Reasonable
       against a theme that cannot tell a pre-order from a sold-out size. This
       one now reads pre-orders from the server render, so the substitute is
       redundant, and the substitute is also what broke the page: the app
       decides which variant is on screen by watching a hidden input's `value`
       attribute, this theme swaps colour by assigning that input's `value`
       property, and a property assignment mutates no attribute. The app never
       hears about the new colour, so the buttons it moved off-screen for the
       old one stay off-screen. That is the missing ADD TO CART.

       What used to be here was a MutationObserver racing the app to put them
       back, and it lost twice over: it watched for `display: none`, which is
       not what the app writes, and even correct it would have been one
       asynchronous callback trying to out-run another.

       product.css §7b pins the buttons visible with `!important` instead, and
       hides the app's injected elements. A stylesheet beats an inline style,
       applies at first paint, and cannot be raced. There is nothing left for
       this element to do about it. */

    syncSticky(variant) {
      if (variant) {
        var text = variant.price_formatted || formatMoney(variant.price, this.moneyFormat);
        /* `[data-atc-price]` joined `[data-sticky-price]` here when the main
           button gained an inline price ("ADD TO CART | $62.00"). Deliberately
           one query and one write rather than a second code path: two places
           formatting the same money from the same variant is exactly how the
           two figures on a page start disagreeing. */
        document
          .querySelectorAll('[data-sticky-price], [data-atc-price]')
          .forEach(function (node) {
            node.textContent = text;
          });
      }
      var title = document.querySelector('[data-sticky-title]');
      if (title && this.titleNode) title.textContent = this.titleNode.textContent.trim();
    }

    setError(message) {
      if (!this.errorNode) return;
      this.errorNode.textContent = message || '';
      this.errorNode.toggleAttribute('hidden', !message);
    }

    /** Recompute everything downstream of the current pill selection. */
    update() {
      this.refreshAvailability();

      var selection = this.selection();
      var complete = this.selectionComplete();
      var variant = complete ? this.matchVariant(selection) : null;
      this.currentVariant = variant;

      this.publishVariant(variant);

      /* With nothing chosen yet, keep showing the product's own price rather
         than blanking it — the shopper still needs to see what it costs. */
      this.setPrice(variant || this.variants[0]);
      this.syncSticky(variant || this.variants[0]);

      if (!complete) {
        this.setButton('incomplete', 'Select a size');
      } else if (!isBuyable(variant)) {
        this.setButton('unavailable', 'Sold out');
      } else if (isPreorder(variant)) {
        /* Sellable, but not from stock. The wording is the merchant's own,
           taken from the pre-order app when its embed is on the page and from
           the theme's own default otherwise, so the button reads the same in
           the preview as it does live. */
        this.setButton('idle', preorderCopy().buttonText);
      } else {
        this.setButton('idle', 'Add to cart');
      }

      /* "Ships on or before October 1", the merchant's own line from the
         campaign. Only on a pre-order variant, and cleared the moment the
         shopper picks an in-stock size — a shipping promise left behind on the
         wrong size is worse than none. */
      if (this.preorderNote) {
        var entry = complete ? preorderFor(variant && variant.id) : null;
        var message = (entry && entry.message) || '';
        this.preorderNote.textContent = message;
        this.preorderNote.hidden = !message;
      }

      if (this.bis) {
        /* Notify-me is for a size nobody can have. A pre-order size can be
           had, so it gets the button, not the waiting list. */
        this.bis.hidden = !(complete && variant && !isBuyable(variant));
      }
    }

    /* ----------------------------------------------------------- behaviour */

    /**
     * Put `?variant=` back after a colour swap.
     *
     * `<kosha-siblings>` rebuilds this element and THEN pushes the new
     * colour's plain product URL, in that order, so the variant parameter this
     * element had just written is wiped a moment after it was written. Rather
     * than have the swatches know about the query string, the element that
     * owns the variant re-states it once the dust has settled. One owner of
     * `?variant=`, and it is the thing that knows which variant it is.
     */
    handleSiblingChanged() {
      this.syncVariantUrl(this.currentVariant);
    }

    handleChange(event) {
      if (!event.target.matches('[data-size-input]')) return;
      this.setError('');
      this.groups.forEach(function (group) {
        group.classList.remove('size-group--error');
      });
      this.update();
    }

    /** No size chosen: point at the first empty group instead of failing mute. */
    promptSize() {
      var target = null;
      this.groups.forEach(function (group) {
        if (target) return;
        if (!group.querySelector('[data-size-input]:checked')) target = group;
      });
      if (!target) return;

      target.classList.add('size-group--error');
      this.setError('Please choose a size.');
      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({
          block: 'center',
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      }
      var first = target.querySelector('[data-size-input]:not([data-unavailable])');
      if (first) first.focus({ preventScroll: true });
    }

    handleSubmit(event) {
      event.preventDefault();
      /* In-flight guard. `pointer-events: none` on the loading button is not
         enough on its own: <kosha-sticky-atc> forwards taps by calling
         `target.click()`, and a programmatic click ignores pointer-events
         entirely. Guarding here covers every entry point — real click,
         forwarded click, and Enter on a focused pill. */
      if (this.pending) return;
      if (!this.selectionComplete()) {
        this.promptSize();
        return;
      }
      var variant = this.currentVariant;
      if (!isBuyable(variant)) return;
      /* BUY IT NOW is a second submit button on this same form (see
         product-info.liquid). `event.submitter` is what separates the two:
         both add one unit, and only this one leaves for checkout afterwards.
         Read here rather than in `addToCart` because `addToCart` is also
         reachable directly, and a direct call is never a buy-now. */
      var submitter = event.submitter;
      var checkout = !!(submitter && submitter.hasAttribute('data-buy-now'));
      this.addToCart(variant, checkout);
    }

    addToCart(variant, checkout) {
      var element = this;
      /* Second gate, because addToCart is reachable directly, not only
         through handleSubmit. */
      if (this.pending) return;
      this.pending = true;
      if (this.button) this.button.setAttribute('data-state', 'loading');
      this.setError('');

      /* LINE ITEM PROPERTIES, and this is not hypothetical.

         This form is submitted by `fetch`, not by the browser, so every field
         an app adds to it — a pre-order's "Ships by" note, an engraving, a gift
         message — was being built out of the request entirely. The order went
         through untagged and nothing anywhere said so. Collecting
         `properties[...]` is what makes any such app work here at all.

         Unchecked boxes and radios are skipped and empty values dropped, which
         is what a native form post does and what keeps Shopify from rendering
         a blank line on the order. */
      var payload = { id: variant.id, quantity: 1 };
      var props = {};
      if (this.form) {
        this.form.querySelectorAll('[name^="properties["]').forEach(function (input) {
          if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
          var field = input.getAttribute('name') || '';
          var key = field.slice(11, field.length - 1);
          if (!key || !input.value) return;
          props[key] = input.value;
        });
      }

      /* THE PRE-ORDER FIELDS ARE THE THEME'S, NOT THE FORM'S.

         Whatever the app may still have written onto this form describes the
         variant it last recognised, which after a colour swap is not the one
         being bought. So every key a pre-order line can carry comes off, and
         the line computed for THIS variant goes back on: `properties[Note] =
         "PRE-ORDER"` when it is a pre-order, nothing when it is not.

         The second half is the one worth stating out loud. An in-stock size
         must not reach the checkout wearing the note left behind by the
         pre-order size chosen before it, and nothing else on the page would
         take it off.

         Everything else the form carries is untouched. */
      var owned = preorderOwnedKeys();
      Object.keys(owned.properties).forEach(function (key) {
        delete props[key];
      });

      var line = preorderLine(variant);
      if (line) {
        Object.keys(line.properties).forEach(function (key) {
          props[key] = line.properties[key];
        });
      }

      if (Object.keys(props).length > 0) payload.properties = props;

      /* A selling plan on the form is a real one (a subscription, say) unless it
         is a pre-order plan, in which case it is the app's stale copy and
         `preorderLine()` decides whether this variant gets one. On this store
         it never does: see preorder-entries.liquid for why not. */
      var plan = this.form && this.form.querySelector('[name="selling_plan"]');
      if (plan && plan.value && !owned.plans[String(plan.value)]) {
        payload.selling_plan = plan.value;
      }
      if (line && line.sellingPlan) payload.selling_plan = line.sellingPlan;

      fetch(cartRoute('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(function (item) {
          /* BUY IT NOW LEAVES BEFORE ASKING ANYTHING ELSE (owner, 2026-09-07:
             "it's takin forever to add to cart and take them to checkout").

             `/cart.js` below is read for one reason: the header's cart count.
             On this path there is no header left to refresh, because the
             browser is going to checkout. Waiting for it put a second full
             round trip between the tap and the redirect, on the one journey
             where every extra moment is a shopper wondering whether it worked.

             So the redirect goes first and that number is never read. It is the
             one count nobody will see.

             The button is deliberately left spinning and `pending` left set:
             the page is on its way out, and a button flicking back to "Add to
             cart" mid-navigation reads as a failed add. `armCheckoutFallback`
             is the cap on that, for when the navigation does not happen. */
          if (checkout) {
            window.location.href = cartRoute('checkout');
            element.armCheckoutFallback();
            return null;
          }

          return fetch(cartRoute('cart.js'), { headers: { Accept: 'application/json' } })
            .then(function (response) {
              return response.json();
            })
            .catch(function () {
              return null;
            })
            .then(function (cart) {
              if (cart) updateCartCount(cart.item_count);
              element.announceAdded(variant, item, cart);
            });
        })
        .catch(function (error) {
          element.setButton('idle', 'Add to cart');
          element.setError(
            (error && (error.description || error.message)) ||
              'Sorry, we could not add that to your cart.'
          );
        })
        /* Released when the request settles, not when the "Added to cart"
           label times out — a shopper should be able to add a second item
           immediately, just not the same one twice from one gesture. */
        .then(function () {
          element.pending = false;
        });
    }

    /**
     * THE SPINNER MUST NOT BE ABLE TO OUTLIVE THE PAGE (owner, 2026-09-07:
     * "when i click buy it now on product page it just sits on this loading
     * add to cart forever").
     *
     * BUY IT NOW deliberately leaves the button spinning and `pending` set: the
     * page is on its way to checkout and a button flicking back to "Add to
     * cart" mid-navigation reads as a failed add. That is right whenever the
     * navigation actually happens, and it is the whole bug when it does not.
     *
     * It does not happen on the local preview. Checkout cannot be served
     * through `shopify theme dev`: `/checkout` there redirects into Shop Pay
     * and ends at a 401 on 127.0.0.1 (traced 2026-09-07). The shopper is left
     * looking at a spinner with no message, no way back, and an item that WAS
     * added. Against the real store it is rarer but not impossible: an offline
     * moment, a blocked redirect, a slow edge.
     *
     * So the promise is capped. Eight seconds is far longer than a checkout
     * redirect takes and short enough that nobody is still waiting on faith. If
     * the page is still here when it fires, the button goes back to whatever
     * `update()` says it should be and the shopper is told the truth: the item
     * is in the cart, checkout did not open. If the navigation succeeds the
     * page unloads and the timer dies with it, unfired.
     *
     * `pageshow` covers the other half of the same problem. A shopper who
     * reaches checkout and presses Back gets this page from the browser's
     * cache exactly as it was left, spinner and all, with `pending` still true
     * so the button is dead. `event.persisted` is that restore and nothing
     * else, so the reset cannot fire on an ordinary load.
     */
    armCheckoutFallback() {
      var element = this;
      if (this.checkoutTimer) clearTimeout(this.checkoutTimer);
      this.checkoutTimer = setTimeout(function () {
        element.pending = false;
        element.update();
        element.setError('Checkout did not open. Your item is in your cart.');
      }, 8000);
    }

    /**
     * Decide which of the two buy shortcuts the shopper gets.
     *
     * WHAT SHOPIFY ACTUALLY PUTS IN THAT SLOT, because the obvious test is
     * wrong. `<shopify-accelerated-checkout>` is server-rendered holding a
     * disabled, transparent SKELETON, and only later swaps in the real thing
     * once it has asked the device what it supports. So "does the slot contain
     * a visible element" answers yes immediately, on every device, before
     * anything has been decided. Read the markup before changing this.
     *
     * It settles one of two ways, and the attribute says so up front:
     * `fallback="{"name":"buy_it_now"}"`. Either a WALLET arrives — Apple Pay,
     * Google Pay, Shop Pay, whichever the device has — or Shopify gives up and
     * renders its own plain "Buy it now", which carries
     * `.shopify-payment-button__button--unbranded`.
     *
     * That second case is the whole reason this method exists. Shopify's
     * fallback is an unstyled button this theme cannot reach into, and the
     * owner asked for her own brown one there (2026-09-07: "default to buy it
     * now if not"). So the unbranded fallback is not shown: Shopify's block is
     * hidden and the theme's button takes the slot.
     *
     *   wallet      keep Shopify's block, theme button stays hidden
     *   unbranded   hide Shopify's block, show the theme's button
     *   never ran   same as unbranded — script blocked, offline, ancient
     *               browser. A shopper must never be left with no way to buy.
     *
     * The theme's button starts hidden and is only ever revealed, never taken
     * away: pulling a full-width button out from under a thumb mid-tap is how
     * somebody buys the wrong thing.
     */
    watchWallet() {
      var fallback = this.querySelector('[data-buy-now]');
      if (!fallback) return;

      var slot = this.querySelector('[data-wallet]');
      if (!slot) {
        /* No wallet markup at all, so the theme's button is the only
           shortcut there is. */
        fallback.hidden = false;
        return;
      }

      var element = this;
      var settled = false;

      function reading() {
        if (slot.querySelector('.shopify-payment-button__button--unbranded')) return 'unbranded';
        if (slot.querySelector('.shopify-payment-button__button--branded')) return 'wallet';
        /* The skeleton is Shopify still deciding. Once it is gone and no
           unbranded button replaced it, a wallet has taken the slot. */
        if (slot.querySelector('.shopify-payment-button__skeleton')) return 'pending';
        return 'wallet';
      }

      function settle(verdict) {
        if (settled) return;
        settled = true;
        if (element.walletTimer) clearTimeout(element.walletTimer);
        if (element.walletObserver) {
          element.walletObserver.disconnect();
          element.walletObserver = null;
        }
        if (verdict === 'wallet') {
          fallback.hidden = true;
          return;
        }
        slot.hidden = true;
        fallback.hidden = false;
      }

      var first = reading();
      if (first !== 'pending') {
        settle(first);
        return;
      }

      if (window.MutationObserver) {
        this.walletObserver = new MutationObserver(function () {
          var now = reading();
          if (now !== 'pending') settle(now);
        });
        this.walletObserver.observe(slot, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      /* Still undecided after this is a device with no wallet, or a script
         that never arrived. Either way the shopper gets a button. */
      this.walletTimer = setTimeout(function () {
        var now = reading();
        settle(now === 'pending' ? 'unbranded' : now);
      }, 2500);
    }

    /** Undo a spinner restored from the browser's back-forward cache. */
    handlePageShow(event) {
      if (!event || !event.persisted) return;
      if (this.checkoutTimer) clearTimeout(this.checkoutTimer);
      this.pending = false;
      this.setError('');
      this.update();
    }

    announceAdded(variant, item, cart) {
      var element = this;
      this.setButton('added', 'Added to cart');
      if (this.resetTimer) clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(function () {
        element.update();
      }, 2400);

      this.dispatchEvent(
        new CustomEvent('kosha:cart:added', {
          bubbles: true,
          composed: true,
          detail: { variant: variant, item: item, cart: cart },
        })
      );
    }

    /* ---------------------------------------------------------- public API */

    /**
     * Swap the element onto a different product. Rebuilds the size pills, the
     * variant map, the price display and the button state.
     *
     * @param {object} productJson - /products/<handle>.js shape
     * @returns {boolean} true when the form was rebuilt
     */
    setProduct(productJson) {
      if (!productJson || typeof productJson !== 'object') return false;
      if (!Array.isArray(productJson.variants)) return false;

      var variants = productJson.variants.map(normalizeVariant).filter(Boolean);
      if (variants.length === 0) return false;

      var options = normalizeOptions(productJson, variants);
      var previous = this.selection();

      this.variants = variants;
      if (this.mapScript) this.mapScript.textContent = JSON.stringify(variants);
      if (productJson.id != null) this.setAttribute('data-product-id', String(productJson.id));
      if (productJson.handle) this.setAttribute('data-product-handle', productJson.handle);
      this.setAttribute('data-option-count', String(options.length));

      this.rebuildGroups(isDefaultOnly(options) ? [] : options, previous);
      this.clearNotifyMe();
      this.setError('');
      this.update();
      return true;
    }

    /**
     * Throw away Postscript's back-in-stock link when the colour changes.
     *
     * Postscript injects its "text me when it's back" link into
     * `#ps__bis_target_container` once, on load, for the product it found on
     * the page, and it publishes no way to ask it to do that again. Colours
     * here change with no page load, so after a swap that link is still the
     * previous colour's — and both colours have to be entirely sold out for it
     * to be on screen at all, which across one colour family is an ordinary
     * situation, not a freak one. A shopper who asked to be told when the
     * Plum came back would have been signed up for the Ivy.
     *
     * There is nothing to re-run, so this fails closed: the link goes, the
     * container loses the class their SDK stamps on it, and product.css's
     * `.bis:not(:has(.ps-bis-custom-container-added))` folds the whole block
     * away again. If their SDK notices the empty container and re-injects, the
     * offer comes back for the right colour; if it does not, the shopper is
     * shown nothing rather than the wrong thing. Offering to text someone about
     * a garment they are not looking at is worse than offering nothing.
     */
    clearNotifyMe() {
      var slot = this.querySelector('#ps__bis_target_container');
      if (!slot) return;
      slot.replaceChildren();
      slot.classList.remove('ps-bis-custom-container-added');
    }

    /**
     * Rebuild the pill groups from cloned <template> markup, carrying the
     * previous selection across by value — a shopper who had "M" chosen keeps
     * "M" on the sibling colour when that size exists.
     */
    rebuildGroups(options, previous) {
      if (!this.groupsHost || !this.templates) return;

      var content = this.templates.content;
      var groupTemplate = content.querySelector('[data-size-group]');
      var pillTemplate = content.querySelector('.pill');
      if (!groupTemplate || !pillTemplate) return;

      var nodes = options.map(function (option, i) {
        var group = groupTemplate.cloneNode(true);
        group.setAttribute('data-option-position', String(option.position));

        var name = group.querySelector('.size-group__name');
        if (name) name.textContent = groupLegend(option.name);

        var carried =
          previous[i] != null && option.values.indexOf(previous[i]) > -1 ? previous[i] : null;
        if (option.values.length === 1) carried = option.values[0];

        var readout = group.querySelector('[data-size-group-chosen]');
        if (readout) readout.textContent = carried || '';

        var pills = group.querySelector('[data-size-pills]');
        option.values.forEach(function (value) {
          var pill = pillTemplate.cloneNode(true);
          var input = pill.querySelector('[data-size-input]');
          input.name = 'option' + option.position;
          input.value = value;
          input.checked = value === carried;
          var label = pill.querySelector('.pill__label');
          if (label) label.textContent = value;
          pills.appendChild(pill);
        });

        return group;
      });

      this.groupsHost.replaceChildren.apply(this.groupsHost, nodes);
      this.groupsHost.setAttribute('data-group-count', String(nodes.length));
      if (this.selector) this.selector.hidden = nodes.length === 0;
    }
  }

  if (!customElements.get('kosha-product-form')) {
    customElements.define('kosha-product-form', KoshaProductForm);
  }

  /* ==========================================================================
     <kosha-sticky-atc>

     The floating cream card pinned to the bottom of the viewport, on BOTH
     breakpoints, once the real add-to-cart button has scrolled off the top.
     Markup and the design rationale: snippets/product-info.liquid.

     It mirrors the real button rather than owning its own form, so there is
     exactly one source of variant truth and exactly one guarded POST path; the
     tap is forwarded with `target.click()` and `KoshaProductForm.pending`
     collapses a burst into one request (see handleSubmit).

     What this element owns, and nothing else does: the thumbnail, the eyebrow,
     the selected-size summary, and the size picker that "Change" opens. The
     first three are computed from the DOM the form already maintains — the
     `<h1>` and the checked size radios — rather than pushed here by the form,
     so `KoshaProductForm` did not have to grow a second display
     responsibility and `syncSticky()` is byte-for-byte what it was.

     The picker owns no state at all: it *borrows the form's own selector*,
     moving that one node into a dock rendered inside `<kosha-product-form>`
     and back again. See the block above `openPanel()`.
     ========================================================================== */

  /* Plural only when the product really has two size groups. A Kanti set asks
     for two; Shakti asks for one, and "Choose your sizes" on a single pair of
     leggings reads like something is missing. */
  function satcPrompt(groupCount) {
    return groupCount > 1 ? 'Choose your sizes' : 'Choose your size';
  }

  /**
   * The small-caps eyebrow, derived from the product title.
   *
   * Four steps, and they are the same four steps the Liquid runs in
   * product-info.liquid. That parity is the whole point: the sibling payload
   * carries no product type, so a swap has to be able to recompute the label
   * from `entry.title` alone and land on the word the server rendered.
   * `product.type` itself is unusable — blank on two of the four test
   * products and plural on a third. Full derivation in the snippet.
   */
  function satcEyebrow(title) {
    var base = String(title || '')
      .split(' - ')[0]
      .split('|')[0]
      .trim();
    if (!base) return '';
    var words = base.split(/\s+/);
    return String(words[words.length - 1] || '').toUpperCase();
  }

  class KoshaStickyAtc extends HTMLElement {
    constructor() {
      super();
      this.panelOpen = false;
      this.panelHadIncomplete = false;
      this.selectorParent = null;
      this.selectorAnchor = null;
      this.lastPointer = 0;
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleSwap = this.handleSwap.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handleCartAdded = this.handleCartAdded.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.syncFrame = 0;
    }

    connectedCallback() {
      /**
       * Hoist to <body>, for the reason `hoistSizeGuide()` records at length:
       * Liquid renders this inside `.product__info`, which is
       * `position: sticky` at >=990px, and **sticky always creates a stacking
       * context**. A `position: fixed` child is then resolved inside it, so
       * `.header`'s root-level z-index 50 wins and the card is capped under
       * the header. It never bit while the bar was mobile-only (the info
       * column is not sticky below 990px); it bites the moment the card is
       * asked to show on desktop, which is what this redesign asks for.
       *
       * The move is a disconnect followed by a re-connect, so the real setup
       * runs on the second pass. `disconnectedCallback` is idempotent, and
       * `product.js` is `defer`red, so the element's children are fully parsed
       * before it upgrades.
       */
      if (this.parentElement && this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }

      this.target = document.querySelector('[data-atc]');
      this.thumb = this.querySelector('[data-satc-thumb]');
      this.eyebrowNode = this.querySelector('[data-satc-eyebrow]');
      this.titleNode = this.querySelector('[data-sticky-title]');
      this.summaryNode = this.querySelector('[data-satc-summary]');
      this.changeBtn = this.querySelector('[data-satc-change]');
      if (!this.target) return;

      /* Delegated on `document`, not on the form: the expanded shopping view
         relocates `<kosha-product-form>` into the lightbox and back, this
         element is no longer even a sibling of it after the hoist, and the
         panel's close button lives in the form's subtree rather than in this
         one. One listener covers all three. */
      document.addEventListener('click', this.handleClick);
      document.addEventListener('change', this.handleChange);
      document.addEventListener('kosha:sibling:changed', this.handleSwap);
      document.addEventListener('kosha:cart:added', this.handleCartAdded);
      /* Always on, not only while the panel is open: it doubles as the
         pointer-vs-keyboard clock `handleChange` reads, and the pointerdown
         that *opens* the panel necessarily happens before any open-time
         listener could exist. */
      document.addEventListener('pointerdown', this.handlePointerDown, true);

      this.refresh();

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleScroll, { passive: true });
      this.sync();

      if (!('IntersectionObserver' in window)) return;
      var element = this;
      this.observer = new IntersectionObserver(
        function () {
          element.sync();
        },
        { threshold: 0 }
      );
      this.observer.observe(this.target);
    }

    /**
     * Show only when the real button has gone off the *top*: a bar that
     * appears before you have reached the button is just noise.
     *
     * The state is computed from the button's rect rather than read off the
     * IntersectionObserver entry, and there is a scroll listener as well as
     * the observer, because **IntersectionObserver does not notify when the
     * intersection state does not change**. Jumping the viewport in one step
     * from "button below the fold" to "button above the fold" never crosses
     * threshold 0 — both states are `isIntersecting: false` — so no callback
     * is queued and the bar is left in the wrong state. Measured: a single
     * `scrollTo` past the button left the card hidden with the button 240px
     * above the viewport top. A continuous human scroll always crosses, which
     * is why the observer alone looked correct for so long; an anchor jump, a
     * back/forward scroll restoration or a "skip to reviews" link does not.
     *
     * The observer is kept because it is what fires the initial evaluation and
     * it costs nothing; the listener is rAF-throttled to one rect read per
     * frame, and it is what makes the contract exact.
     */
    sync() {
      if (!this.target) return;
      var rect = this.target.getBoundingClientRect();
      var viewport = window.innerHeight || document.documentElement.clientHeight || 0;
      var onScreen = rect.bottom > 0 && rect.top < viewport;
      var shouldHide = onScreen || rect.top > 0;
      if (this.hidden === shouldHide) return;
      this.hidden = shouldHide;
      /* The card going away takes the picker with it. This is also what keeps
         the column honest: the panel holds the *real* selector, so leaving it
         open while the shopper scrolls back to the pills would show them an
         empty size block. The bar hides at exactly the moment the real button
         (and the selector above it) returns to view. */
      if (shouldHide) this.closePanel(false);
    }

    handleScroll() {
      if (this.syncFrame) return;
      var element = this;
      var run = function () {
        element.syncFrame = 0;
        element.sync();
      };
      this.syncFrame =
        typeof requestAnimationFrame === 'function' ? requestAnimationFrame(run) : setTimeout(run, 16);
    }

    disconnectedCallback() {
      /* Never leave the page's only size selector parked in a panel that is
         about to be detached. Same defence `unmountBuyColumn()` runs. */
      this.closePanel(false);
      document.removeEventListener('click', this.handleClick);
      document.removeEventListener('change', this.handleChange);
      document.removeEventListener('kosha:sibling:changed', this.handleSwap);
      document.removeEventListener('kosha:cart:added', this.handleCartAdded);
      document.removeEventListener('keydown', this.handleKeydown, true);
      document.removeEventListener('pointerdown', this.handlePointerDown, true);
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleScroll);
      if (this.syncFrame && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(this.syncFrame);
      }
      this.syncFrame = 0;
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    /* ----------------------------------------------------------- rendering */

    /** The live product form, wherever the expanded view has parked it. */
    get form() {
      return document.querySelector('kosha-product-form');
    }

    /**
     * Recompute every part of the card the form does not already write.
     *
     * Cheap enough to run on every size click: one `querySelectorAll` over at
     * most two groups, plus three text writes. Deliberately reads the DOM
     * instead of caching, because `setProduct()` replaces the group elements
     * wholesale on a colour swap.
     */
    refresh() {
      var form = this.form;
      var groups = form ? form.querySelectorAll('[data-size-group]') : [];

      var heading = document.querySelector('.product__title');
      var title = heading ? heading.textContent.trim() : '';
      /* `syncSticky()` already writes this from the same node; restated here
         so the card is correct even on the first paint, before any variant
         change has gone through the form. Same value, so the two can never
         disagree. */
      if (this.titleNode && title) this.titleNode.textContent = title;

      if (this.eyebrowNode) {
        var eyebrow = satcEyebrow(title || (this.titleNode ? this.titleNode.textContent : ''));
        this.eyebrowNode.textContent = eyebrow;
        this.eyebrowNode.hidden = !eyebrow;
      }

      /* "plain" is the no-size-groups product (grip socks, or a colour that
         has only a default variant): nothing to choose, so the summary slot
         shows the price and the "Change" link is not rendered at all. */
      this.setAttribute('data-satc-mode', groups.length ? 'sizes' : 'plain');

      if (!this.summaryNode || groups.length === 0) return;

      var picks = [];
      var incomplete = false;
      Array.prototype.forEach.call(groups, function (group) {
        var checked = group.querySelector('[data-size-input]:checked');
        if (checked) picks.push(checked.value);
        else incomplete = true;
      });

      if (incomplete || picks.length === 0) {
        this.summaryNode.textContent = satcPrompt(groups.length);
        this.summaryNode.setAttribute('data-satc-incomplete', '');
      } else {
        this.summaryNode.textContent = picks.join(' / ');
        this.summaryNode.removeAttribute('data-satc-incomplete');
      }
    }

    /* ----------------------------------------------------------- behaviour */

    /** Mark the group still waiting, or clear the mark when none is. */
    markNextGroup(target) {
      this.groupNodes().forEach(function (group) {
        if (group === target) group.setAttribute('data-satc-next', '');
        else group.removeAttribute('data-satc-next');
      });
    }

    handleChange(event) {
      if (!event.target || typeof event.target.matches !== 'function') return;
      if (!event.target.matches('[data-size-input]')) return;
      this.refresh();
      if (!this.panelOpen) return;

      var groups = this.groupNodes();
      var incomplete = this.incompleteGroups();

      if (incomplete.length > 0) {
        /* A set with one size still open: stay put and move to the group that
           is still waiting, so the second choice is one tap or one arrow away. */
        this.panelHadIncomplete = true;
        this.focusFirstIncomplete();
        return;
      }

      this.markNextGroup(null);

      /**
       * THE RE-PICK CASE, and it is the one the owner hit on her own phone.
       *
       * A shopper who arrives on a variant URL — from a collection card, from
       * a shared link, from the back button — lands with every group already
       * chosen. Opening the picker to CHANGE the top size then completed the
       * selection on the very first tap, so the panel shut with the bottom
       * size still unvisited, which reads exactly like "it closed on me".
       *
       * So on a multi-group product the panel closes only on the tap that
       * COMPLETES a selection that was incomplete when the panel opened. If it
       * opened complete there is no completing moment to close on, and the
       * shopper says when she is done: the ADD TO CART button, the close
       * button, Escape, or a tap outside. A single-group product still closes
       * on any pick — there is nothing else in there to strand her in.
       */
      if (groups.length > 1 && !this.panelHadIncomplete) return;

      /**
       * Complete. Close and hand focus to ADD TO CART, which is what the
       * shopper wants next.
       *
       * ...but only if the choice came from a pointer. Radio groups fire
       * `change` on every arrow key, so closing on the first `change`
       * unconditionally would slam the panel shut the moment a keyboard user
       * started browsing the sizes — one arrow press into a single-group
       * product and the picker is gone. 600ms since the last pointerdown
       * inside the open panel is the signal for "this was a tap"; a keyboard
       * user keeps the panel and closes it with Escape or the close button.
       */
      if (Date.now() - this.lastPointer > 600) return;
      this.closePanel(false);
      var button = this.querySelector('[data-sticky-btn]');
      if (button && typeof button.focus === 'function') button.focus({ preventScroll: true });
    }

    /**
     * A sibling colour swap. `apply()` has already written the `<h1>` and
     * rebuilt the pills by the time this event fires, so the title, the
     * eyebrow and the summary all fall out of one `refresh()`.
     *
     * The thumbnail is the only thing that needs the payload: `entry.media[0]`
     * is the swapped colour's featured image, and it is the *same* URL
     * `kosha-gallery.setMedia()` just put in the hero, so the browser serves
     * it from cache and the swap still issues zero extra requests.
     */
    handleSwap(event) {
      var entry = event ? event.detail : null;
      var media = entry && Array.isArray(entry.media) ? entry.media[0] : null;
      if (this.thumb && this.thumb.tagName === 'IMG' && media && media.src) {
        this.thumb.src = media.src;
      }
      this.refresh();
      /* `setProduct()` rebuilds the groups in place, so an open panel simply
         re-fills with the new colour's pills and the selection the form
         carried across. The one case that cannot stay open is a sibling with
         no groups at all, where the panel would be an empty sheet. */
      if (this.panelOpen && this.groupNodes().length === 0) this.closePanel(false);
    }

    /* ------------------------------------------------------- the size panel

       "Change" no longer scrolls back to the pills. It opens a compact picker
       that expands upward from the card, and the picker holds the ONE real
       `[data-size-selector]` — moved here on open, moved home on close, the
       way `mountBuyColumn()` moves the buy column into the lightbox. Nothing
       is mirrored and nothing is copied, so there is exactly one owner of the
       selection, the availability narrowing, the struck sold-out pills and the
       legend readouts: `<kosha-product-form>`, unchanged.

       The dock is rendered inside the form for that reason (full derivation in
       product-info.liquid): `KoshaProductForm.groups` is
       `this.querySelectorAll(...)` and its `change` listener is on the element,
       so a selector parked outside the form would silently report "no groups"
       and stop reporting changes.
       --------------------------------------------------------------------- */

    /** Queried fresh: the form can be relocated into the expanded view. */
    get panel() {
      var form = this.form;
      return form ? form.querySelector('[data-satc-panel]') : null;
    }

    openPanel() {
      if (this.panelOpen) return;
      var form = this.form;
      var panel = this.panel;
      var body = panel ? panel.querySelector('[data-satc-panel-body]') : null;
      var selector = form ? form.querySelector('[data-size-selector]') : null;
      if (!form || !panel || !body || !selector) return;
      /* Nothing to pick: a default-variant product has no groups, and opening
         an empty sheet is worse than doing nothing. */
      if (selector.querySelectorAll('[data-size-group]').length === 0) return;

      /* Taking the selector out of the column makes the document ~65px
         shorter, and the browser answers that with scroll anchoring or a
         clamp — measured as a 63-69px jump at both widths before this guard.
         A picker that scrolls the page is the exact thing this panel exists to
         stop doing, so the move is pinned the way `holdScroll()` pins a
         colour swap. */
      var anchorY = window.scrollY;
      this.selectorParent = selector.parentNode;
      this.selectorAnchor = selector.nextSibling;
      body.appendChild(selector);
      this.holdScroll(anchorY);

      var title = panel.querySelector('[data-satc-panel-title]');
      if (title) {
        title.textContent = satcPrompt(selector.querySelectorAll('[data-size-group]').length);
      }

      panel.setAttribute('data-open', '');
      this.panelOpen = true;
      /* Whether this OPENING started with something still to choose. It is the
         whole of the close policy below — see `handleChange()`. */
      this.panelHadIncomplete = this.incompleteGroups().length > 0;
      if (this.changeBtn) this.changeBtn.setAttribute('aria-expanded', 'true');

      /* Not a modal: no scroll lock and no focus trap. It is an anchored
         disclosure over its own trigger, and trapping the page for a two-pill
         choice is heavier than the interaction deserves. Escape and an
         outside tap both close it, which is the contract for this pattern. */
      document.addEventListener('keydown', this.handleKeydown, true);

      this.focusFirstIncomplete();
    }

    closePanel(returnFocus) {
      if (!this.panelOpen) return;
      this.panelOpen = false;
      this.markNextGroup(null);

      var anchorY = window.scrollY;
      var panel = this.panel;
      var selector = panel ? panel.querySelector('[data-size-selector]') : null;
      var parent = this.selectorParent;
      if (selector && parent) {
        var anchor = this.selectorAnchor;
        /* The anchor is a sibling captured at open time. It cannot move on any
           path this element allows, but restoring to the end of the form is a
           better failure than throwing and stranding the selector in a hidden
           panel. */
        if (anchor && anchor.parentNode === parent) parent.insertBefore(selector, anchor);
        else parent.appendChild(selector);
      }
      this.selectorParent = null;
      this.selectorAnchor = null;
      this.holdScroll(anchorY);

      if (panel) panel.removeAttribute('data-open');
      if (this.changeBtn) this.changeBtn.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.handleKeydown, true);

      if (returnFocus && this.changeBtn && document.contains(this.changeBtn)) {
        this.changeBtn.focus({ preventScroll: true });
      }
    }

    /**
     * Pin the viewport across a relocation, then again on the next frame to
     * catch what the browser does after layout. Identical in shape and in
     * reasoning to `<kosha-siblings>.holdScroll()`, including `behavior:
     * 'auto'` — the theme sets a global `html { scroll-behavior: smooth }`, so
     * a bare `scrollTo` would *animate* the correction and turn a 65px jump
     * into a 65px slide.
     */
    holdScroll(y) {
      var restore = function () {
        if (Math.abs(window.scrollY - y) < 1) return;
        window.scrollTo({ top: y, left: window.scrollX, behavior: 'auto' });
      };
      restore();
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(restore);
    }

    /** The groups currently on the page, wherever they are parked. */
    groupNodes() {
      var form = this.form;
      return form ? Array.prototype.slice.call(form.querySelectorAll('[data-size-group]')) : [];
    }

    /** The groups with nothing chosen in them yet, in document order. */
    incompleteGroups() {
      return this.groupNodes().filter(function (group) {
        return !group.querySelector('[data-size-input]:checked');
      });
    }

    /**
     * Focus the first group still waiting on a choice, else the first group,
     * and mark it so the eye follows the focus.
     *
     * `data-satc-next` is the visual half of "you still have one to go": on a
     * set, tapping the top size and getting nothing but a focus ring is a very
     * quiet answer on a phone, where there is no focus ring at all until a
     * keyboard appears. product.css §2k-2 paints it in the product's own
     * accent. It is cleared the moment nothing is waiting.
     */
    focusFirstIncomplete() {
      var groups = this.groupNodes();
      if (groups.length === 0) return;
      var target = null;
      groups.forEach(function (group) {
        if (target) return;
        if (!group.querySelector('[data-size-input]:checked')) target = group;
      });
      this.markNextGroup(target);
      if (!target) target = groups[0];

      var input =
        target.querySelector('[data-size-input]:checked') ||
        target.querySelector('[data-size-input]:not([data-unavailable])') ||
        target.querySelector('[data-size-input]');
      if (input && typeof input.focus === 'function') input.focus({ preventScroll: true });
    }

    handleKeydown(event) {
      if (!this.panelOpen) return;
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      event.preventDefault();
      event.stopPropagation();
      this.closePanel(true);
    }

    /**
     * Outside tap closes. Also the timestamp the auto-close heuristic reads —
     * see `handleChange`. Capture phase, so it runs before anything on the
     * page can stop propagation.
     */
    handlePointerDown(event) {
      this.lastPointer = Date.now();
      if (!this.panelOpen) return;
      var node = event.target;
      if (node && typeof node.closest === 'function') {
        if (node.closest('[data-satc-panel]') || node.closest('[data-sticky-atc]')) return;
      }
      this.closePanel(false);
    }

    handleCartAdded() {
      this.closePanel(false);
    }

    handleClick(event) {
      if (!event.target || typeof event.target.closest !== 'function') return;

      if (event.target.closest('[data-satc-panel-close]')) {
        event.preventDefault();
        this.closePanel(true);
        return;
      }

      if (event.target.closest('[data-satc-change]')) {
        event.preventDefault();
        if (this.panelOpen) this.closePanel(true);
        else this.openPanel();
        return;
      }

      if (!event.target.closest('[data-sticky-btn]')) return;
      event.preventDefault();

      /* Tapping ADD TO CART with a size still missing opens the picker instead
         of forwarding. Forwarding would reach `KoshaProductForm.promptSize()`,
         which scrolls the page back to the pills — the behaviour this panel
         replaces. The main button in the column keeps that scroll, and should:
         its pills are three centimetres above it, not a screen away. */
      var form = this.form;
      if (
        form &&
        typeof form.selectionComplete === 'function' &&
        !form.selectionComplete() &&
        this.groupNodes().length > 0
      ) {
        this.openPanel();
        return;
      }

      /* Everything else is forwarded, so the add still goes through the one
         guarded path: `handleSubmit` -> `this.pending` -> a single POST. */
      if (this.target) this.target.click();
    }
  }

  if (!customElements.get('kosha-sticky-atc')) {
    customElements.define('kosha-sticky-atc', KoshaStickyAtc);
  }

  /* ==========================================================================
     <kosha-siblings>   (Task 6)

     Instant colour switching with zero navigation.

     The swatches are real <a href> links, so the no-JS / crawler / Cmd-click
     paths are exactly what they were. This element intercepts plain left
     clicks only, and only for a handle that is present in the embedded
     payload; anything else falls through to a normal page load.

     A swap is: h1 → gallery.setMedia() → form.setProduct() → colour name,
     final-sale flag, reassurance line, review badge, aria-current →
     history.pushState + document.title → `kosha:sibling:changed`.

     Emits: `kosha:sibling:changed`  (bubbling + composed)
            detail = the payload entry that was swapped in.
            Task 7 (upsells) and Task 8 (reviews) listen for it.
     ========================================================================== */

  var PREFETCH_IMAGE_BUDGET = 12;

  function handleFromPath(path) {
    var match = /\/products\/([^/?#]+)/.exec(path || '');
    return match ? match[1] : null;
  }

  /**
   * Read one option value off a payload variant. The payload omits `option2` /
   * `option3` when they are null (Task 6 §3), so an absent key is meaningful
   * and must not be coerced to the string "undefined".
   */
  function variantOptionAt(variant, index) {
    if (!variant) return null;
    var value = variant['option' + (index + 1)];
    return value == null ? null : String(value);
  }

  /** The chosen size per option group; null where nothing is chosen yet. */
  function sizeSelection() {
    var form = document.querySelector('kosha-product-form');
    if (!form) return [];
    return Array.prototype.map.call(form.querySelectorAll('[data-size-group]'), function (group) {
      var checked = group.querySelector('[data-size-input]:checked');
      return checked ? checked.value : null;
    });
  }

  /**
   * Can this colour be bought in the sizes currently chosen?
   *
   * Nothing chosen  → product-level availability (only a colour that is sold
   *                   out in every size gets marked).
   * Partly chosen   → filter on the chosen groups only. A Kanti set has two
   *                   size groups; picking a crop size but not a legging size
   *                   must ask "does this colour have that crop size in *any*
   *                   legging size", not "is that exact pair in stock".
   * Fully chosen    → the exact combination has to exist and be available.
   *
   * All of it comes off the payload each entry already carries, so there is no
   * fetch and no second source of truth.
   *
   * Selection indices past the sibling's own option count are ignored. Every
   * colour inside one family has the same group shape today, so this is a
   * guard rather than a behaviour; without it a two-group selection projected
   * onto a one-group sibling would compare "M" against nothing and strike the
   * whole row.
   */
  function entryAvailableFor(entry, selection) {
    if (!entry) return true;
    var groupCount = Array.isArray(entry.options) ? entry.options.length : 0;
    var picks = [];
    selection.forEach(function (value, i) {
      if (value == null) return;
      if (groupCount && i >= groupCount) return;
      picks.push({ index: i, value: value });
    });

    if (picks.length === 0 || !Array.isArray(entry.variants)) {
      return entry.available !== false;
    }

    return entry.variants.some(function (variant) {
      if (variant.available === false) return false;
      return picks.every(function (pick) {
        return variantOptionAt(variant, pick.index) === pick.value;
      });
    });
  }

  /**
   * Split a string around a product title so the surrounding template survives
   * a swap. Used for `document.title` and for `og:title`, both of which a
   * merchant can reshape with an SEO template we should not assume the shape
   * of.
   */
  function splitAround(text, name) {
    var at = name ? text.indexOf(name) : -1;
    if (at < 0) return { prefix: '', suffix: '', matched: false };
    return { prefix: text.slice(0, at), suffix: text.slice(at + name.length), matched: true };
  }

  function headNode(name) {
    return document.querySelector('[data-kosha-head="' + name + '"]');
  }

  class KoshaSiblings extends HTMLElement {
    constructor() {
      super();
      this.entries = [];
      this.byHandle = {};
      this.current = null;
      this.handleClick = this.handleClick.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
      this.handleSizeChange = this.handleSizeChange.bind(this);
    }

    connectedCallback() {
      /**
       * Re-entry guard.
       *
       * The expanded shopping view relocates this element into the lightbox
       * and back, and a DOM move is a disconnect followed by a connect. Every
       * `captureBaseline()` value is a *landing colour* baseline — the
       * document-title template, the reassurance line, and above all the
       * Product Details copy that a payload entry with no `description` key
       * means "restore". Recomputing them from whatever colour happens to be
       * on screen would quietly redefine what "the original" is, and the next
       * swap to an inheriting colour would restore the wrong copy.
       *
       * So a re-connect only re-attaches listeners. Nothing is re-read.
       */
      if (this.ready) {
        this.attachListeners();
        return;
      }

      this.payloadScript = this.querySelector('[data-siblings]');
      this.finalSaleTemplate = this.querySelector('[data-final-sale-template]');
      this.reassuranceTemplate = this.querySelector('[data-reassurance-final-sale]');
      this.currentLabel = this.querySelector('[data-swatch-current]');

      this.entries = this.readPayload();
      if (this.entries.length < 2) return;

      var byHandle = {};
      this.entries.forEach(function (entry) {
        byHandle[entry.handle] = entry;
      });
      this.byHandle = byHandle;

      var active = this.querySelector('[data-swatch][aria-current]');
      this.current =
        (active && active.getAttribute('data-handle')) ||
        handleFromPath(window.location.pathname) ||
        this.entries[0].handle;

      this.adoptRenderedGallery();
      this.captureBaseline();
      this.refreshSwatchAvailability();

      this.attachListeners();
      this.ready = true;

      this.prefetch();
    }

    /* All three take the same bound reference every time, so re-attaching on
       a re-connect cannot stack duplicates. */
    attachListeners() {
      this.addEventListener('click', this.handleClick);
      window.addEventListener('popstate', this.handlePopState);
      /* On `document`, not on the form: the size pills are rebuilt from a
         template on every swap, so anything bound to the pills themselves
         would be thrown away by the first colour change. The listener reads
         `:checked` straight off the DOM, so it does not care whether
         <kosha-product-form> has processed the same event yet. */
      document.addEventListener('change', this.handleSizeChange);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      window.removeEventListener('popstate', this.handlePopState);
      document.removeEventListener('change', this.handleSizeChange);
    }

    /* ---------------------------------------------------------------- data */

    readPayload() {
      if (!this.payloadScript) return [];
      var parsed;
      try {
        parsed = JSON.parse(this.payloadScript.textContent);
      } catch (error) {
        return [];
      }
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (entry) {
        return (
          entry &&
          entry.handle &&
          entry.url &&
          Array.isArray(entry.media) &&
          entry.media.length > 0 &&
          Array.isArray(entry.variants) &&
          entry.variants.length > 0
        );
      });
    }

    /**
     * The payload caps each colour at 8 images to stay lean, but the colour
     * that was server-rendered already has its *complete* media list sitting
     * in the gallery's own payload. Adopt it, so pressing Back onto the
     * landing colour restores all 12 Kanti images rather than the first 8.
     * Read once, at upgrade time, before any swap can rewrite that script.
     */
    adoptRenderedGallery() {
      var entry = this.byHandle[this.current];
      var script = document.querySelector('kosha-gallery [data-gallery-media]');
      if (!entry || !script) return;
      try {
        var full = JSON.parse(script.textContent);
        if (Array.isArray(full) && full.length > entry.media.length) entry.media = full;
      } catch (error) {
        /* Keep the payload's copy. */
      }
    }

    /**
     * Everything the swap has to be able to put back. The document title is
     * split around the *current* product title rather than assumed to be
     * "<title> – <shop>", so a merchant changing the SEO template does not
     * silently break the pushState title.
     */
    captureBaseline() {
      var entry = this.byHandle[this.current];
      var name = entry ? entry.title : '';

      var docParts = splitAround(document.title, name);
      this.titlePrefix = docParts.prefix;
      this.titleSuffix = docParts.suffix;

      var reassurance = document.querySelector('[data-slot="reassurance"]');
      this.reassuranceNode = reassurance;
      this.reassuranceDefault = reassurance ? reassurance.innerHTML : '';

      /* The landing colour's Product Details copy. A payload entry with no
         `description` key means "identical to this", which is how eleven of
         Shakti's twelve colours pay nothing for the feature — see the comment
         in sibling-swatches.liquid. */
      var description = document.querySelector('[data-product-description]');
      this.descriptionNode = description;
      this.descriptionDefault = description ? description.innerHTML : '';
      this.descriptionDetails = description ? description.closest('details') : null;

      /* Head tags. `og:title` gets the same prefix/suffix treatment as
         `document.title` rather than being overwritten wholesale, so an SEO
         template ("Kosha Fit | <product>") survives a swap. */
      this.headCanonical = headNode('canonical');
      this.headOgUrl = headNode('og-url');
      this.headOgTitle = headNode('og-title');
      this.headTwitterTitle = headNode('twitter-title');
      this.headOgPrice = headNode('og-price');
      this.headOgImage = headNode('og-image');
      this.headOgImageHttps = headNode('og-image-https');
      this.headOgImageW = headNode('og-image-w');
      this.headOgImageH = headNode('og-image-h');

      var ogTitle = this.headOgTitle ? this.headOgTitle.getAttribute('content') || '' : '';
      var ogParts = splitAround(ogTitle, name);
      this.ogTitlePrefix = ogParts.prefix;
      this.ogTitleSuffix = ogParts.suffix;
    }

    /* ----------------------------------------------------------- behaviour */

    handleClick(event) {
      if (event.defaultPrevented) return;
      /* Let the browser own every gesture that means "somewhere else":
         middle click, and every modified click (new tab / new window /
         download / Firefox's alt-click). */
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      var link = event.target.closest('[data-swatch]');
      if (!link || !this.contains(link)) return;

      var handle = link.getAttribute('data-handle');
      if (handle === this.current) {
        event.preventDefault();
        return;
      }

      /* No payload entry (merchant list longer than the payload cap, or a
         sibling with no media) → do not intercept. A real navigation is the
         correct, still-working outcome. */
      var entry = this.byHandle[handle];
      if (!entry) return;

      event.preventDefault();
      this.swap(entry, true);
    }

    handlePopState() {
      var handle = handleFromPath(window.location.pathname);
      if (!handle || handle === this.current) return;

      var entry = this.byHandle[handle];
      if (!entry) {
        /* Went back to a colour we cannot rebuild — hand it to the network
           rather than leaving the URL and the DOM disagreeing. */
        window.location.reload();
        return;
      }
      this.swap(entry, false);
    }

    /* ---------------------------------------------------------- the swap */

    swap(entry, push) {
      var element = this;
      var run = function () {
        element.apply(entry, push);
      };

      if (!prefersReducedMotion() && typeof document.startViewTransition === 'function') {
        try {
          var transition = document.startViewTransition(run);
          /**
           * A ViewTransition hands back three promises, and an aborted
           * transition rejects them. Nothing here awaits any of them, so an
           * abort surfaced as an "Uncaught (in promise) InvalidStateError:
           * Transition was aborted because of invalid state" in the console —
           * reproducible by swapping colour from inside the expanded shopping
           * view, where the document changes under the snapshot.
           *
           * The abort is harmless: `run()` has already applied the swap, the
           * transition just does not animate. Acknowledging the rejections is
           * the whole fix; swallowing them silently is correct here precisely
           * because there is no failure to report.
           */
          if (transition) {
            ['ready', 'finished', 'updateCallbackDone'].forEach(function (key) {
              var promise = transition[key];
              if (promise && typeof promise.catch === 'function') promise.catch(function () {});
            });
          }
          return;
        } catch (error) {
          /* Fall through to the plain swap. */
        }
      }
      run();
    }

    apply(entry, push) {
      var gallery = document.querySelector('kosha-gallery');
      var form = document.querySelector('kosha-product-form');

      /* Pre-flight before touching anything, so a refusal never leaves half a
         product on screen. */
      var ready =
        gallery &&
        typeof gallery.setMedia === 'function' &&
        form &&
        typeof form.setProduct === 'function';
      if (!ready) {
        window.location.href = entry.url;
        return false;
      }

      /* A colour swap has no business moving the viewport. Every known mover is
         fixed at the root (see keepThumbVisible/setSparse and the reserved
         badge row in product.css); this is the guard that catches anything a
         later task reintroduces. */
      var anchorY = window.scrollY;

      /* Title before setProduct: syncSticky() reads the h1 for the mobile
         bar's label. */
      var titleNode = document.querySelector('.product__title');
      if (titleNode) titleNode.textContent = entry.title;

      if (!gallery.setMedia(entry.media) || !form.setProduct(entry)) {
        window.location.href = entry.url;
        return false;
      }

      this.setActiveSwatch(entry);
      this.setStage(entry);
      this.setFinalSale(entry);
      this.setRating(entry);
      this.setDescription(entry);
      this.holdScroll(anchorY);

      this.current = entry.handle;
      /* After `current` moves, because the "is this colour buyable in the
         chosen size" question is asked of every swatch including the new one,
         and after setProduct() has carried the size selection across. */
      this.refreshSwatchAvailability();

      document.title = this.titlePrefix + entry.title + this.titleSuffix;
      this.setHead(entry);
      if (push && typeof history.pushState === 'function') {
        history.pushState({ koshaSibling: entry.handle }, '', entry.url);
      }

      this.dispatchEvent(
        new CustomEvent('kosha:sibling:changed', {
          bubbles: true,
          composed: true,
          detail: entry,
        })
      );
      return true;
    }

    /**
     * Pin the viewport across the swap, then again on the next frame to catch
     * anything the browser does after layout (scroll anchoring, a clamp when
     * the new colour makes the document shorter).
     *
     * `behavior: 'auto'` is not optional: the theme sets a global
     * `html { scroll-behavior: smooth }`, so a bare `scrollTo` would *animate*
     * the correction — which is precisely how the original defect looked, a
     * slow slide of the whole page. If the document genuinely cannot reach the
     * old offset any more the browser clamps, which is the right answer.
     */
    holdScroll(y) {
      var restore = function () {
        if (Math.abs(window.scrollY - y) < 1) return;
        window.scrollTo({ top: y, left: window.scrollX, behavior: 'auto' });
      };
      restore();
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(restore);
    }

    setActiveSwatch(entry) {
      this.querySelectorAll('[data-swatch]').forEach(function (link) {
        if (link.getAttribute('data-handle') === entry.handle) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
      if (this.currentLabel) this.currentLabel.textContent = entry.colorName || '';
    }

    /**
     * Follow the swapped colour on the one thing still derived from it: the
     * accent system.
     *
     * All five values are written as custom properties on `<html>`, the same
     * element Liquid's <style> block writes them on, so the initial render and
     * every swap share one owner. `accentSystem()` is byte-identical to the Liquid arithmetic in
     * sections/main-product.liquid — see the block comment above it. An
     * `entry` with no `colorHex` (or a malformed one) falls back to the brand
     * cocoa, which is the same thing the server renders for a product with no
     * `custom.color` metafield.
     *
     * The transition is CSS's, not this function's: the properties change
     * instantly and every rule that reads one transitions its own
     * background/border/colour over `--ease` (240ms). A custom property cannot
     * be transitioned itself, which is exactly why the tokens are consumed by
     * ordinary properties everywhere rather than being interpolated here.
     */
    setStage(entry) {
      /* `<html>`, because the sticky bar and the size-guide drawer are both
         children of <body> rather than of the section — see the block comment
         in sections/main-product.liquid, which writes the same five here. */
      var host = document.documentElement;
      var system = accentSystem(entry.colorHex);
      host.style.setProperty('--pdp-accent', system.accent);
      host.style.setProperty('--pdp-on-accent', system.on);
      host.style.setProperty('--pdp-accent-ink', system.ink);
      host.style.setProperty('--pdp-accent-hover', system.hover);
      host.style.setProperty('--pdp-accent-muted', system.muted);

      /* The tone flag lives on the SECTION rather than on <html>, because it
         is a branch for rules scoped to `.product` and nothing outside the
         section reads it. `system.on` is the cream label exactly when the fill
         is dark enough to carry one — the same test main-product.liquid makes
         in Liquid. */
      var section = document.querySelector('[data-product-section]');
      if (section) {
        section.setAttribute('data-accent-tone', system.on === '#fefcfa' ? 'dark' : 'light');
      }
    }

    /**
     * Final-sale flag and the reassurance line move together: "free returns
     * and exchanges" is not true of a final-sale colour, and showing both at
     * once is worse than showing neither.
     */
    setFinalSale(entry) {
      var row = document.querySelector('.product__price-row');
      var flag = row ? row.querySelector('.final-sale') : null;

      if (row) {
        if (entry.finalSale && !flag && this.finalSaleTemplate) {
          row.appendChild(this.finalSaleTemplate.content.cloneNode(true));
        } else if (!entry.finalSale && flag) {
          flag.remove();
        }
      }

      if (!this.reassuranceNode) return;
      if (entry.finalSale && this.reassuranceTemplate) {
        this.reassuranceNode.replaceChildren(this.reassuranceTemplate.content.cloneNode(true));
      } else if (this.reassuranceNode.innerHTML !== this.reassuranceDefault) {
        /* Guarded because the overwhelmingly common case is "no colour on this
           store is final sale", where the line is already the default and
           re-assigning `innerHTML` would reparse it on every single swap for
           no change at all. */
        this.reassuranceNode.innerHTML = this.reassuranceDefault;
      }
    }

    /* ------------------------------------------- size-aware swatch state */

    handleSizeChange(event) {
      var target = event.target;
      if (!target || typeof target.matches !== 'function') return;
      if (!target.matches('[data-size-input]')) return;
      this.refreshSwatchAvailability();
    }

    /**
     * Mark the colours that cannot be bought in the sizes currently chosen.
     *
     * This is styling and labelling only — never `disabled`, never
     * `pointer-events: none`. A shopper who wants to *look* at Cherry after
     * choosing a size Cherry does not stock still gets to look at Cherry, and
     * the swatch is still a real link for the crawler, the middle-click and
     * the shared URL.
     *
     * Swatches with no payload entry (a colour past `max_payload`) are left
     * entirely alone: we have no variant data for them, and guessing would be
     * worse than saying nothing.
     */
    refreshSwatchAvailability() {
      var element = this;
      var selection = sizeSelection();
      var chosen = selection.filter(function (value) {
        return value != null;
      });
      var chosenLabel = chosen.join(' / ');

      this.querySelectorAll('[data-swatch]').forEach(function (link) {
        var entry = element.byHandle[link.getAttribute('data-handle')];
        if (!entry) {
          link.removeAttribute('data-unavailable');
          link.removeAttribute('aria-label');
          return;
        }

        var buyable = entryAvailableFor(entry, selection);
        link.toggleAttribute('data-unavailable', !buyable);

        if (buyable) {
          /* Back to the element's own content as its accessible name — the
             visually-hidden colour name the snippet renders. */
          link.removeAttribute('aria-label');
          return;
        }

        var name = entry.colorName || link.getAttribute('title') || '';
        link.setAttribute(
          'aria-label',
          chosenLabel ? name + ', unavailable in ' + chosenLabel : name + ', sold out'
        );
      });
    }

    /* --------------------------------------------- per-colour description */

    /**
     * Swap the Product Details copy.
     *
     * `entry.description === undefined` means the sibling's copy is identical
     * to the landing colour's, so the baseline goes back in. An explicit empty
     * string means the colour genuinely has none, and the whole `<details>`
     * collapses — which is why product-accordions.liquid now renders that
     * group unconditionally instead of skipping it: `setDescription()` can
     * only rewrite a host that exists.
     */
    setDescription(entry) {
      if (!this.descriptionNode) return;

      var html = entry.description == null ? this.descriptionDefault : entry.description;
      if (this.descriptionNode.innerHTML !== html) this.descriptionNode.innerHTML = html;

      if (this.descriptionDetails) {
        this.descriptionDetails.toggleAttribute('hidden', String(html).trim() === '');
      }

      /* Reset the read-more clamp. A shopper who expanded Azalea's copy should
         not land on Black already expanded — the new copy is a different
         length, and `syncClamp()` refuses to re-measure an expanded clamp. */
      this.descriptionNode.removeAttribute('data-clamp-expanded');
      var panel = this.descriptionNode.closest('.accordion__panel');
      var toggle = panel ? panel.querySelector('[data-clamp-toggle]') : null;
      if (toggle) toggle.textContent = 'Read more';
      syncClamp(this.descriptionNode);
    }

    /* ------------------------------------------------ head reconciliation */

    /**
     * Keep the head tags that name a product in step with the URL.
     *
     * The new canonical is resolved *against the existing one*, not against
     * `location.origin`: `canonical_url` renders the shop's primary domain
     * (https://koshafit.com/...) while the page itself may be served from a
     * dev proxy or a myshopify.com host. Rebuilding from `location` would
     * quietly rewrite the canonical origin to whatever host the shopper
     * happens to be on, which is worse than the staleness it fixes.
     */
    setHead(entry) {
      var base =
        (this.headCanonical && this.headCanonical.getAttribute('href')) ||
        (this.headOgUrl && this.headOgUrl.getAttribute('content')) ||
        window.location.href;

      var href = null;
      try {
        href = new URL(entry.url, base).href;
      } catch (error) {
        href = null;
      }

      if (href) {
        if (this.headCanonical) this.headCanonical.setAttribute('href', href);
        if (this.headOgUrl) this.headOgUrl.setAttribute('content', href);
      }

      var title = this.ogTitlePrefix + entry.title + this.ogTitleSuffix;
      if (this.headOgTitle) this.headOgTitle.setAttribute('content', title);
      if (this.headTwitterTitle) this.headTwitterTitle.setAttribute('content', title);

      if (this.headOgPrice && typeof entry.price === 'number') {
        this.headOgPrice.setAttribute('content', (entry.price / 100).toFixed(2));
      }

      var hero = Array.isArray(entry.media) ? entry.media[0] : null;
      if (!hero || !hero.src) return;
      /* Payload sources are protocol-relative (`//cdn…`), matching what
         `image_url` gives Liquid; og:image wants an absolute URL and the
         server render supplies the scheme the same way. */
      var relative = hero.src.indexOf('//') === 0;
      if (this.headOgImage) {
        this.headOgImage.setAttribute('content', relative ? 'http:' + hero.src : hero.src);
      }
      if (this.headOgImageHttps) {
        this.headOgImageHttps.setAttribute('content', relative ? 'https:' + hero.src : hero.src);
      }
      if (this.headOgImageW) this.headOgImageW.setAttribute('content', String(hero.width));
      if (this.headOgImageH) this.headOgImageH.setAttribute('content', String(hero.height));
    }

    /**
     * Judge.me preview badge. Only the numbers are swapped — product.css
     * hides the whole row off `data-average-rating='0.00'`, which is the same
     * convention Task 5 established, so a zero-review colour hides itself
     * without a second mechanism. Full review-section reconciliation is
     * Task 8; this keeps the badge from lying in the meantime.
     */
    setRating(entry) {
      var row = document.querySelector('[data-slot="rating"]');
      var badge = row ? row.querySelector('.jdgm-prev-badge') : null;
      if (!badge) return;

      var rating = entry.rating || {};
      var average = Number(rating.average) || 0;
      var count = Number(rating.count) || 0;
      var score = average.toFixed(2);

      badge.setAttribute('data-average-rating', score);
      badge.setAttribute('data-number-of-reviews', String(count));

      var stars = badge.querySelector('.jdgm-prev-badge__stars');
      if (stars) {
        stars.setAttribute('data-score', score);
        stars.setAttribute('aria-label', score + ' stars');
        stars.querySelectorAll('.jdgm-star').forEach(function (star, i) {
          var state = 'jdgm--off';
          if (average >= i + 0.75) state = 'jdgm--on';
          else if (average >= i + 0.25) state = 'jdgm--half';
          star.className = 'jdgm-star ' + state;
        });
      }

      var text = badge.querySelector('.jdgm-prev-badge__text');
      if (text) text.textContent = count === 1 ? ' 1 review ' : ' ' + count + ' reviews ';
    }

    /* ------------------------------------------------------------ prefetch */

    /**
     * Warm the first two images of every other colour while the main thread is
     * idle, so the first swap paints from cache.
     *
     * `sizes` and `srcset` are copied from the real gallery image rather than
     * setting a bare `src`: without them the browser downloads the widest
     * candidate, the gallery then picks a narrower one, and the prefetch is
     * pure waste. With them, both resolve to the same URL.
     */
    prefetch() {
      var connection = navigator.connection;
      if (connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''))) {
        return;
      }

      var reference = document.querySelector('[data-gallery-slide] img');
      var sizes = reference ? reference.getAttribute('sizes') : null;
      var element = this;

      var run = function () {
        var budget = PREFETCH_IMAGE_BUDGET;
        element.entries.forEach(function (entry) {
          if (entry.handle === element.current) return;
          entry.media.slice(0, 2).forEach(function (media) {
            if (budget <= 0) return;
            budget -= 1;
            var image = new Image();
            image.decoding = 'async';
            if (sizes) {
              image.sizes = sizes;
              image.srcset = buildSrcset(media.src, media.width, SRCSET_WIDTHS);
            }
            image.src = media.src;
          });
        });
      };

      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(run, { timeout: 4000 });
      } else {
        setTimeout(run, 2000);
      }
    }
  }

  if (!customElements.get('kosha-siblings')) {
    customElements.define('kosha-siblings', KoshaSiblings);
  }

  /* ==========================================================================
     <kosha-upsell>   (Task 7)

     "Complete the set": a scroll-snap row of cross-sell cards, one shared
     size panel, and the social-proof bar. Server-rendered by
     snippets/complete-the-set.liquid; nothing is emitted when the product
     carries no `theme.upsell_list`, so this element simply never upgrades on
     those pages.

     Public interface:

       upsell.setUpsells(upsellArray)   // → boolean

     `upsellArray` is Task 6's `event.detail.upsells`:
       [{ handle, title, price, variantId, image }]   (price in cents)

     The element also subscribes to `kosha:sibling:changed` on `document`
     itself, so a colour swap keeps the row in sync with no wiring in
     <kosha-siblings>. Task 6's payload caps upsells at 2 per colour and
     carries no variant list, so a swapped-in card resolves its variants
     lazily from /products/<handle>.js the first time someone taps ADD — see
     `resolveCard`. That fetch is on a user gesture, never on the swap, which
     is why it cannot violate Task 6's "zero /products/ requests on swap"
     guarantee.

     Adds use the same in-flight guard shape as Task 5's I2 fix: one boolean
     on the element gating every entry point, released when the request
     settles rather than when the confirmation label times out. It is
     deliberately a *separate* flag from <kosha-product-form>'s — those are
     two different adds, and blocking one on the other would be a bug.
     ========================================================================== */

  var UPSELL_WIDTHS = [160, 240, 320, 480, 640];

  /* The confirmation *transition* is the 240ms motion token (CSS). The label
     is then held for 2400ms, matching <kosha-product-form>'s "Added to cart",
     because a 240ms label is not readable — the token governs the animation,
     not how long the word stays on screen. */
  var UPSELL_ADDED_HOLD_MS = 2400;

  /** Swap the `width` parameter on a Shopify CDN URL. */
  function upsellSrc(src, width) {
    try {
      var url = new URL(src, window.location.href);
      if (!url.searchParams.has('width')) return src;
      url.searchParams.delete('height');
      url.searchParams.delete('crop');
      url.searchParams.set('width', String(width));
      return url.href;
    } catch (error) {
      return src;
    }
  }

  class KoshaUpsell extends HTMLElement {
    constructor() {
      super();
      this.pending = false;
      this.cache = {};
      this.trigger = null;
      this.activeCard = null;
      this.activeData = null;
      this.resetTimers = [];
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onSiblingChanged = this.onSiblingChanged.bind(this);
    }

    connectedCallback() {
      if (this.upgraded) return;
      this.upgraded = true;

      this.row = this.querySelector('[data-cts-row]');
      this.panel = this.querySelector('[data-cts-panel]');
      this.panelTitle = this.querySelector('[data-cts-panel-title]');
      this.panelGroups = this.querySelector('[data-cts-panel-groups]');
      this.panelAdd = this.querySelector('[data-cts-panel-add]');
      this.panelAddText = this.querySelector('[data-cts-panel-add-text]');
      this.panelPreorder = this.querySelector('[data-cts-panel-preorder]');
      this.status = this.querySelector('[data-cts-status]');
      this.templates = this.querySelector('[data-cts-templates]');

      /* Card ceiling. Liquid owns it (`max_cards` in complete-the-set.liquid)
         and publishes it here, so the server render, the swap rebuild and
         `max_upsells` in sibling-swatches.liquid cannot drift apart. The
         literal is only a floor for a hand-written host with no attribute. */
      this.maxCards = parseInt(this.getAttribute('data-max-cards'), 10) || 4;

      /* <kosha-product-form> already carries the shop's money format; reusing
         it avoids a second Liquid attribute that could drift out of step. */
      var moneyHost = document.querySelector('[data-money-format]');
      this.moneyFormat =
        (moneyHost && moneyHost.getAttribute('data-money-format')) || '${{amount}}';

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('change', this.onChange);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('kosha:sibling:changed', this.onSiblingChanged);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('kosha:sibling:changed', this.onSiblingChanged);
      this.resetTimers.forEach(clearTimeout);
      this.resetTimers = [];
    }

    /* ------------------------------------------------------------- plumbing */

    clone(selector) {
      if (!this.templates) return null;
      var node = this.templates.content.querySelector(selector);
      return node ? node.cloneNode(true) : null;
    }

    cards() {
      return this.row ? Array.prototype.slice.call(this.row.children) : [];
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }

    /* ---------------------------------------------------------- card state */

    /**
     * Variant data for a card, or null when it has to be fetched.
     * Server-rendered multi-variant cards ship a [data-cts-variants] script;
     * swap-built cards ship nothing and land in `this.cache` after the first
     * resolve.
     */
    cardData(card) {
      var handle = card.getAttribute('data-handle');
      if (handle && this.cache[handle]) return this.cache[handle];

      var script = card.querySelector('[data-cts-variants]');
      if (!script) return null;
      var parsed;
      try {
        parsed = JSON.parse(script.textContent);
      } catch (error) {
        return null;
      }
      if (!parsed || !Array.isArray(parsed.variants)) return null;

      var data = {
        legends: Array.isArray(parsed.options) ? parsed.options.map(String) : [],
        variants: parsed.variants.map(normalizeVariant).filter(Boolean),
      };
      if (data.variants.length === 0) return null;
      if (handle) this.cache[handle] = data;
      return data;
    }

    /**
     * Fill in the variant data a swap-built card does not have, from
     * /products/<handle>.js. Resolves to the same shape `cardData` returns,
     * or null.
     */
    resolveCard(card) {
      var element = this;
      var handle = card.getAttribute('data-handle');
      if (!handle) return Promise.resolve(null);
      if (this.cache[handle]) return Promise.resolve(this.cache[handle]);

      return fetch(cartRoute('products/' + handle + '.js'), {
        headers: { Accept: 'application/json' },
      })
        .then(function (response) {
          if (!response.ok) throw new Error('not ok');
          return response.json();
        })
        .then(function (json) {
          var variants = (json.variants || []).map(normalizeVariant).filter(Boolean);
          if (variants.length === 0) return null;
          var options = normalizeOptions(json, variants);
          var data = {
            legends: isDefaultOnly(options)
              ? []
              : options.map(function (option) {
                  return groupLegend(option.name);
                }),
            variants: variants,
          };
          element.cache[handle] = data;
          return data;
        })
        .catch(function () {
          return null;
        });
    }

    /* ------------------------------------------------------------- handlers */

    onClick(event) {
      var close = event.target.closest('[data-cts-panel-close]');
      if (close) {
        this.closePanel(true);
        return;
      }

      var panelConfirm = event.target.closest('[data-cts-panel-add]');
      if (panelConfirm) {
        this.confirmPanel();
        return;
      }

      var add = event.target.closest('[data-cts-add]');
      if (add) {
        event.preventDefault();
        this.handleAdd(add);
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (this.panel && !this.panel.hidden) {
        event.stopPropagation();
        this.closePanel(true);
      }
    }

    onChange(event) {
      if (!event.target.matches('[data-cts-input]')) return;
      this.syncPanel();
    }

    /* Outside click closes the panel. Focus is not returned here — the
       shopper moved the pointer somewhere else on purpose, and yanking focus
       back to the card would fight that. Escape and the close button do
       return it. */
    onDocumentClick(event) {
      if (!this.panel || this.panel.hidden) return;
      if (this.contains(event.target)) return;
      this.closePanel(false);
    }

    onSiblingChanged(event) {
      var detail = event && event.detail;
      this.setUpsells(detail ? detail.upsells : []);
    }

    /* ------------------------------------------------------------ add flow */

    handleAdd(button) {
      if (this.pending) return;
      var card = button.closest('[data-cts-card]');
      if (!card || card.getAttribute('data-available') === 'false') return;

      var directId = card.getAttribute('data-variant-id');
      if (directId) {
        this.addToCart(card, { id: Number(directId), title: '' }, button);
        return;
      }

      var data = this.cardData(card);
      if (data) {
        this.openPanel(card, button, data);
        return;
      }

      /* Swap-built card: no variant list yet. The fetch itself is guarded so
         a rapid double-tap cannot issue two of them. */
      var element = this;
      this.pending = true;
      this.setButtonState(button, 'loading');
      this.resolveCard(card)
        .then(function (resolved) {
          element.pending = false;
          element.setButtonState(button, 'idle');
          if (!resolved) {
            element.announce('Sorry, that item is not available right now.');
            return;
          }
          if (resolved.legends.length === 0 || resolved.variants.length === 1) {
            card.setAttribute('data-variant-id', String(resolved.variants[0].id));
            element.handleAdd(button);
            return;
          }
          element.openPanel(card, button, resolved);
        })
        .catch(function () {
          element.pending = false;
          element.setButtonState(button, 'idle');
        });
    }

    addToCart(card, variant, button) {
      var element = this;
      /* Second gate: addToCart is reachable from the panel as well as from a
         card button, so guarding only handleAdd would leave a hole. */
      if (this.pending) return;
      this.pending = true;
      this.setButtonState(button, 'loading');

      var title = card.getAttribute('data-title') || '';
      /* When the panel drove the add, the panel is about to close — so the
         "Added ✓" confirmation has to land on the card button that opened it,
         not on a control the shopper can no longer see. */
      var feedback = this.trigger || button;

      /* A pre-order bought from this row is the same cart line as one bought
         from the buy column: `properties[Note] = "PRE-ORDER"`, so the drawer
         and the checkout say so. Nothing is added for an in-stock piece. */
      var body = { id: variant.id, quantity: 1 };
      var preorder = preorderLine(variant);
      if (preorder) {
        body.properties = preorder.properties;
        if (preorder.sellingPlan) body.selling_plan = preorder.sellingPlan;
      }

      fetch(cartRoute('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(function (item) {
          return fetch(cartRoute('cart.js'), { headers: { Accept: 'application/json' } })
            .then(function (response) {
              return response.json();
            })
            .catch(function () {
              return null;
            })
            .then(function (cart) {
              if (cart) updateCartCount(cart.item_count);
              element.closePanel(false);
              element.setButtonState(button, 'idle');
              element.confirmAdded(feedback, title, variant.title);
              element.dispatchEvent(
                new CustomEvent('kosha:cart:added', {
                  bubbles: true,
                  composed: true,
                  detail: { variant: variant, item: item, cart: cart, source: 'upsell' },
                })
              );
            });
        })
        .catch(function (error) {
          element.setButtonState(button, 'idle');
          element.announce(
            (error && (error.description || error.message)) ||
              'Sorry, we could not add that to your cart.'
          );
        })
        .then(function () {
          element.pending = false;
        });
    }

    confirmAdded(button, title, variantTitle) {
      var element = this;
      this.setButtonState(button, 'added');
      this.announce(
        title + (variantTitle ? ', ' + variantTitle : '') + ' added to your cart.'
      );
      var timer = setTimeout(function () {
        element.setButtonState(button, 'idle');
      }, UPSELL_ADDED_HOLD_MS);
      this.resetTimers.push(timer);
    }

    setButtonState(button, state) {
      if (!button) return;
      button.setAttribute('data-state', state);
      var text = button.querySelector('[data-cts-add-text], [data-cts-panel-add-text]');
      if (!text) return;
      var isPanel = button.hasAttribute('data-cts-panel-add');

      text.replaceChildren();
      if (state === 'added') {
        /* "Added" is the accessible name; the tick is decorative so a screen
           reader does not read "Added check mark". */
        text.appendChild(document.createTextNode('Added '));
        var tick = document.createElement('span');
        tick.setAttribute('aria-hidden', 'true');
        tick.textContent = '✓';
        text.appendChild(tick);
        return;
      }
      if (state === 'loading') {
        text.textContent = 'Adding…';
        return;
      }
      text.textContent = isPanel ? 'Add to cart' : 'Add';
    }

    /* ---------------------------------------------------------- size panel */

    openPanel(card, button, data) {
      if (!this.panel || !this.panelGroups) return;

      this.closePanel(false);
      this.activeCard = card;
      this.activeData = data;
      this.trigger = button;

      if (this.panelTitle) {
        this.panelTitle.textContent = card.getAttribute('data-title') || '';
      }

      var groups = data.legends.map(function (legend, index) {
        var group = this.clone('[data-cts-group]');
        if (!group) return null;
        group.setAttribute('data-option-index', String(index));
        var name = group.querySelector('.size-group__name');
        if (name) name.textContent = legend;
        var pills = group.querySelector('[data-cts-pills]');
        var seen = [];
        data.variants.forEach(function (variant) {
          var value = variant.options[index];
          if (value == null || seen.indexOf(value) !== -1) return;
          seen.push(value);
          var pill = this.clone('.pill');
          if (!pill || !pills) return;
          var input = pill.querySelector('[data-cts-input]');
          input.name = 'cts-option-' + index;
          input.value = value;
          pill.querySelector('.pill__label').textContent = value;
          pills.appendChild(pill);
        }, this);
        return group;
      }, this);

      this.panelGroups.replaceChildren();
      groups.forEach(function (group) {
        if (group) this.panelGroups.appendChild(group);
      }, this);

      /* Single-value groups (a one-size garment inside a set) are chosen for
         the shopper — there is no decision to make. */
      this.panelGroups.querySelectorAll('[data-cts-group]').forEach(function (group) {
        var inputs = group.querySelectorAll('[data-cts-input]');
        if (inputs.length === 1) inputs[0].checked = true;
      });

      this.panel.hidden = false;
      if (button.hasAttribute('aria-expanded')) button.setAttribute('aria-expanded', 'true');
      this.syncPanel();

      var first = this.panelGroups.querySelector(
        '[data-cts-input]:not([data-unavailable])'
      );
      if (first) first.focus({ preventScroll: true });
      this.panel.scrollIntoView({
        block: 'nearest',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    closePanel(returnFocus) {
      if (!this.panel || this.panel.hidden) return;
      this.panel.hidden = true;
      if (this.trigger) {
        if (this.trigger.hasAttribute('aria-expanded')) {
          this.trigger.setAttribute('aria-expanded', 'false');
        }
        if (returnFocus) this.trigger.focus({ preventScroll: true });
      }
      this.trigger = null;
      this.activeCard = null;
      this.activeData = null;
    }

    panelSelection() {
      return Array.prototype.map.call(
        this.panelGroups.querySelectorAll('[data-cts-group]'),
        function (group) {
          var checked = group.querySelector('[data-cts-input]:checked');
          return checked ? checked.value : null;
        }
      );
    }

    panelVariant() {
      if (!this.activeData) return null;
      var selection = this.panelSelection();
      if (selection.some(function (value) { return value == null; })) return null;
      var hit = null;
      this.activeData.variants.forEach(function (variant) {
        if (hit) return;
        var match = selection.every(function (value, i) {
          return variant.options[i] === value;
        });
        if (match) hit = variant;
      });
      return hit;
    }

    /**
     * Recompute what is reachable given the other groups' current selection,
     * mirror the chosen value into each legend, and set the button state.
     * Same narrowing rule as <kosha-product-form>: a value is struck when no
     * *available* variant combines it with what is already chosen.
     */
    syncPanel() {
      if (!this.activeData || !this.panelGroups) return;
      var data = this.activeData;
      var selection = this.panelSelection();

      this.panelGroups.querySelectorAll('[data-cts-group]').forEach(function (group) {
        var index = parseInt(group.getAttribute('data-option-index'), 10) || 0;
        group.querySelectorAll('[data-cts-input]').forEach(function (input) {
          var reachable = data.variants.some(function (variant) {
            /* `isBuyable`, not `available`: a pre-order size can be bought, so
               striking it through would be a lie, and the same rule already
               governs the pills in the buy column. */
            if (!isBuyable(variant)) return false;
            if (variant.options[index] !== input.value) return false;
            return selection.every(function (value, i) {
              return i === index || value == null || variant.options[i] === value;
            });
          });
          input.toggleAttribute('data-unavailable', !reachable);
          var pill = input.closest('.pill');
          if (pill) pill.classList.toggle('pill--unavailable', !reachable);
        });
        var chosen = group.querySelector('[data-cts-group-chosen]');
        if (chosen) chosen.textContent = selection[index] == null ? '' : selection[index];
      });

      var variant = this.panelVariant();

      /* THE SHIP-BY LINE, in the one place a shopper can still buy a pre-order
         without ever seeing the buy column.

         Owner, 2026-09-05: "it also needs to say in the complete your set part
         if there's a pre-order". A garment offered here at $148 with nothing
         said about it, which then turns up in the cart marked PRE-ORDER, is the
         worst order of finding out. Same sentence, same source, same moment in
         the flow as the buy column's own line: it appears when a pre-order size
         is chosen and goes the instant an in-stock one is. */
      var entry = preorderFor(variant && variant.id);
      if (this.panelPreorder) {
        this.panelPreorder.textContent = (entry && entry.message) || '';
        this.panelPreorder.hidden = !(entry && entry.message);
      }

      if (!this.panelAdd) return;
      if (!variant) {
        this.panelAdd.setAttribute('data-state', 'incomplete');
        this.panelAddText.textContent = 'Select a size';
      } else if (!isBuyable(variant)) {
        this.panelAdd.setAttribute('data-state', 'unavailable');
        this.panelAddText.textContent = 'Sold out';
      } else if (entry) {
        this.panelAdd.setAttribute('data-state', 'idle');
        this.panelAddText.textContent = preorderCopy().buttonText;
      } else {
        this.panelAdd.setAttribute('data-state', 'idle');
        this.panelAddText.textContent = 'Add to cart';
      }
    }

    confirmPanel() {
      if (this.pending) return;
      var variant = this.panelVariant();
      var card = this.activeCard;
      if (!card) return;
      if (!variant) {
        this.announce('Please choose a size.');
        var first = this.panelGroups.querySelector('[data-cts-input]:not(:checked)');
        if (first) first.focus({ preventScroll: true });
        return;
      }
      if (!isBuyable(variant)) return;
      this.addToCart(card, variant, this.panelAdd);
    }

    /* ---------------------------------------------------------- public API */

    /**
     * Rebuild the card row from a Task 6 sibling payload.
     *
     * Capped at `this.maxCards` (from `data-max-cards`, written by Liquid) so
     * a swapped row can never be a different length from what the same
     * product would render server-side.
     *
     * @param {Array} upsells - [{ handle, title, price, variantId, image }]
     * @returns {boolean} true when the row was rebuilt with at least one card
     */
    setUpsells(upsells) {
      if (!this.row || !this.templates) return false;
      this.closePanel(false);

      var list = Array.isArray(upsells) ? upsells : [];
      var element = this;
      var nodes = [];

      list.slice(0, this.maxCards).forEach(function (entry) {
        if (!entry || !entry.handle) return;

        /* A FULLY SOLD-OUT PRODUCT IS NOT OFFERED AT ALL (owner, 2026-09-07:
           "if the entire product is sold out, it doesn't show on the complete
           your set page"). It used to build a card with a disabled "Sold out"
           button, which spent one of four slots on something she cannot sell.

           `=== false`, not `!== true`: an entry that carries no `available`
           key at all is treated as available, so a payload built by an older
           render can never empty this row. Only an explicit false is a skip.

           A PRE-ORDER IS NOT SKIPPED. The pre-order app keeps its variants
           overselling and Shopify therefore reports them as available, which
           is the correct answer — the garment can be bought today.

           The payload is already filtered the same way where it is built
           (snippets/sibling-swatches.liquid), and so is the server render
           (snippets/complete-the-set.liquid). This is the third of the three,
           and it is the one that decides what a colour swap shows. Because
           `this.hidden` is set from `nodes.length` below, a colour whose every
           partner is sold out now hides the module instead of showing a row of
           dead cards. */
        if (entry.available === false) return;

        var card = element.clone('[data-cts-card]');
        if (!card) return;

        card.setAttribute('data-handle', String(entry.handle));
        card.setAttribute('data-title', String(entry.title || ''));

        /* Always true now that a sold-out entry never reaches here, but still
           written rather than left off: `handleAdd` bails on
           `data-available="false"` and reads the attribute on every card, so a
           swap-built card has to state it the way a server-rendered one does. */
        card.setAttribute('data-available', 'true');

        var url = cartRoute('products/' + entry.handle);
        card.querySelectorAll('a').forEach(function (link) {
          link.setAttribute('href', url);
        });
        var title = card.querySelector('.cts__title');
        if (title) title.textContent = String(entry.title || '');

        var price = card.querySelector('[data-price-current]');
        if (price) price.textContent = formatMoney(entry.price, element.moneyFormat);

        var img = card.querySelector('.cts__img');
        if (img && entry.image) {
          img.src = upsellSrc(entry.image, 320);
          var srcset = buildSrcset(entry.image, 640, UPSELL_WIDTHS);
          if (srcset) img.setAttribute('srcset', srcset);
        } else if (img) {
          img.remove();
        }

        var context = card.querySelector('[data-cts-add-context]');
        if (context) context.textContent = ' ' + String(entry.title || '');

        /* The disabled "Sold out" mirror that used to live here is gone with
           the cards it described: nothing sold out is cloned any more, and
           `[data-cts-templates]` is a static <template> in
           snippets/complete-the-set.liquid — an enabled button labelled "Add",
           never a copy of a rendered card — so there is no sold-out state to
           undo either. */

        /* The payload has no variant list, only `selected_or_first_available`.
           Trusting it for a sized garment would silently add someone an
           arbitrary size, so the card stays "resolve on tap" (see
           `resolveCard`) unless a later payload starts carrying variants. */
        nodes.push(card);
      });

      this.row.replaceChildren.apply(this.row, nodes);
      this.hidden = nodes.length === 0;
      this.row.setAttribute(
        'aria-label',
        'Complete the set, ' + nodes.length + ' product' + (nodes.length === 1 ? '' : 's')
      );
      return nodes.length > 0;
    }
  }

  if (!customElements.get('kosha-upsell')) {
    customElements.define('kosha-upsell', KoshaUpsell);
  }

  /* ==========================================================================
     <kosha-set-addon>   snippets/set-addon.liquid

     The matching piece, under the buy buttons. One photograph, two badges, a
     size popup, and an add.

     It adds BOTH garments as one request: this page's product in the size
     chosen for it, and the matching piece in the size chosen for that. Never
     the merchant's bundle SKU — sizes, stock and returns all want to be per
     garment.

     Its first size group MIRRORS the page's own pills rather than duplicating
     their state: picking there forwards the click to the matching
     `[data-size-input]` inside `<kosha-product-form>`, so the page and the
     popup cannot disagree and the form's availability narrowing keeps running
     untouched. The mirror is one-way on purpose; the page is the owner.
     ========================================================================== */

  class KoshaSetAddon extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleSwap = this.handleSwap.bind(this);
    }

    connectedCallback() {
      this.trigger = this.querySelector('[data-set-addon-open]');
      this.panel = this.querySelector('[data-set-addon-panel]');
      this.addButton = this.querySelector('[data-set-addon-add]');
      this.addText = this.querySelector('[data-set-addon-add-text]');
      this.errorNode = this.querySelector('[data-set-addon-error]');
      this.preorderNode = this.querySelector('[data-set-addon-preorder]');
      this.thisChosenNode = this.querySelector('[data-set-addon-this-chosen]');
      this.priceNode = this.querySelector('[data-set-addon-price]');
      this.totalNode = this.querySelector('[data-set-addon-total]');
      this.shipNode = this.querySelector('[data-set-addon-ship]');
      this.imageNode = this.querySelector('.set-addon__img');
      this.thisPills = this.querySelector('[data-set-addon-this] .size-group__pills');
      this.partnerPills = this.querySelector('[data-set-addon-input]')
        ? this.querySelector('[data-set-addon-input]').closest('.size-group__pills')
        : null;
      this.colourMap = this.readMap();
      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
      this.freeShipFloor = parseInt(this.getAttribute('data-free-ship-floor'), 10) || 0;
      this.partnerChosenNode = this.querySelector('[data-set-addon-partner-chosen]');
      this.payload = this.readPayload();

      /* A card that cannot deliver what it offers should not be on the page. */
      if (!this.payload || !this.trigger || !this.panel) {
        this.remove();
        return;
      }

      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      this.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('kosha:sibling:changed', this.handleSwap);
      this.watchArrival();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('kosha:sibling:changed', this.handleSwap);
      if (this.resetTimer) clearTimeout(this.resetTimer);
      if (this.seenObserver) {
        this.seenObserver.disconnect();
        this.seenObserver = null;
      }
    }

    /**
     * `data-seen` the first time the card is actually on screen, then stop
     * watching. The animation is in product.css and runs once: a card sitting
     * under ADD TO CART that kept pulsing would be competing with the buy
     * button, which is the one control on this page that must always win.
     *
     * No observer, no flag, and the card is simply there — the CSS animation
     * is keyed off the attribute rather than off page load, so nothing is
     * hidden waiting for script that might not arrive.
     */
    watchArrival() {
      if (typeof IntersectionObserver !== 'function') {
        this.setAttribute('data-seen', '');
        return;
      }
      var element = this;
      this.seenObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            element.setAttribute('data-seen', '');
            if (element.seenObserver) {
              element.seenObserver.disconnect();
              element.seenObserver = null;
            }
          });
        },
        { threshold: 0.4 }
      );
      this.seenObserver.observe(this);
    }

    readMap() {
      var script = this.querySelector('[data-set-addon-map]');
      if (!script) return {};
      try {
        var parsed = JSON.parse(script.textContent);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (error) {
        return {};
      }
    }

    /**
     * Follow a colour swap. `<kosha-siblings>` changes the product with no
     * navigation, so without this the card keeps offering the colour the
     * shopper just left — the exact bug the owner hit: Azalea's brown leggings
     * still on screen after switching to Black.
     *
     * The partner handle is the one thing that cannot be derived at runtime
     * (`set-map` is Liquid), so it is carried in `[data-set-addon-map]`.
     * Everything else — image, price, sizes — comes from one product fetch.
     *
     * The card hides itself for the duration rather than showing a half-built
     * offer, and stays hidden when the new colour has no partner at all.
     */
    handleSwap(event) {
      var entry = event && event.detail;
      if (!entry || !entry.handle) return;

      var partnerHandle = this.colourMap[entry.handle];
      if (!partnerHandle) {
        this.hidden = true;
        return;
      }

      var element = this;
      var token = (this.swapToken = (this.swapToken || 0) + 1);
      this.setOpen(false);
      this.hidden = true;

      fetch(cartRoute('products/' + partnerHandle + '.js'), {
        headers: { Accept: 'application/json' },
      })
        .then(function (response) {
          if (!response.ok) throw new Error('unavailable');
          return response.json();
        })
        .then(function (json) {
          /* A shopper can outrun a fetch by clicking three colours quickly;
             only the most recent one is allowed to land. */
          if (token !== element.swapToken) return;
          if (element.rebuild(entry, json)) element.hidden = false;
        })
        .catch(function () {
          /* Left hidden. An add-on that might be the wrong garment is worse
             than no add-on. */
        });
    }

    /** @returns {boolean} true when the card was rebuilt and can be shown. */
    rebuild(entry, json) {
      if (!json || !Array.isArray(json.variants) || json.variants.length === 0) return false;
      if (!Array.isArray(json.options) || json.options.length !== 1) return false;

      var variants = json.variants.map(function (variant) {
        return {
          id: variant.id,
          value: variant.option1,
          available: variant.available !== false,
        };
      });
      if (!variants.some(function (v) { return v.available; })) return false;

      var thisVariants = (entry.variants || []).map(function (variant) {
        return {
          id: variant.id,
          value: variant.option1,
          available: variant.available !== false,
        };
      });
      if (thisVariants.length === 0) return false;

      var combined = (entry.price || 0) + (json.price || 0);
      var name = String(json.title || '').split(' - ')[0].trim();

      this.payload = {
        handle: json.handle,
        title: json.title,
        name: name,
        combined: combined,
        variants: variants,
        thisVariants: thisVariants,
      };

      var money = formatMoney(combined, this.moneyFormat);
      if (this.priceNode) this.priceNode.textContent = money;
      if (this.totalNode) this.totalNode.textContent = money;
      if (this.shipNode) this.shipNode.hidden = combined < this.freeShipFloor;

      if (this.imageNode && json.featured_image) {
        this.imageNode.removeAttribute('srcset');
        this.imageNode.src = json.featured_image;
        this.imageNode.alt = json.title || '';
      }

      this.renderPills(this.partnerPills, variants, 'kosha_set_addon_size', 'data-set-addon-input');
      this.renderPills(this.thisPills, thisVariants, 'kosha_set_addon_this', 'data-set-addon-this-input');

      var legend = this.querySelector('.set-addon__group:last-of-type .size-group__name');
      if (legend) legend.textContent = name;

      this.syncReadouts();
      this.setError('');
      this.setAddState('idle', 'Add to cart');
      return true;
    }

    /* Built here rather than cloned from a <template>, because a pill is four
       elements and a template for it would be a fifth thing to keep in step
       with size-selector.liquid. The class names are the ones product.css
       already styles. */
    renderPills(host, variants, name, hook) {
      if (!host) return;
      var nodes = variants.map(function (variant) {
        var label = document.createElement('label');
        label.className = 'pill' + (variant.available ? '' : ' pill--unavailable');

        var input = document.createElement('input');
        input.className = 'pill__input visually-hidden';
        input.type = 'radio';
        input.name = name;
        input.value = variant.value;
        input.setAttribute(hook, '');
        if (!variant.available) input.setAttribute('data-unavailable', '');

        var text = document.createElement('span');
        text.className = 'pill__label';
        text.textContent = variant.value;

        label.appendChild(input);
        label.appendChild(text);
        return label;
      });
      host.replaceChildren.apply(host, nodes);
    }

    readPayload() {
      var script = this.querySelector('[data-set-addon-partner]');
      if (!script) return null;
      try {
        var parsed = JSON.parse(script.textContent);
        if (!parsed || !Array.isArray(parsed.variants) || parsed.variants.length === 0) {
          return null;
        }
        return parsed;
      } catch (error) {
        return null;
      }
    }

    get open() {
      /* `data-open` on the host, not `hidden` on the panel. The panel stopped
         carrying `hidden` when the card was made to animate open — a hidden
         element has no height to animate from — and this getter was left
         reading it, so it answered "open" every single time and the toggle
         always resolved to close. The button did nothing. One attribute, and
         it is the same one product.css already keys the whole expansion off. */
      return this.hasAttribute('data-open');
    }

    match(list, value) {
      var found = null;
      (list || []).forEach(function (variant) {
        if (!found && variant.value === value) found = variant;
      });
      return found;
    }

    get chosen() {
      var input = this.querySelector('[data-set-addon-input]:checked');
      return input ? this.match(this.payload.variants, input.value) : null;
    }

    get chosenThis() {
      var input = this.querySelector('[data-set-addon-this-input]:checked');
      return input ? this.match(this.payload.thisVariants, input.value) : null;
    }

    /** The page's own pills, which are the single owner of this product's size. */
    get pageInputs() {
      var form = document.querySelector('[data-product-form]');
      if (!form) return [];
      return Array.prototype.slice.call(form.querySelectorAll('[data-size-input]'));
    }

    /** Carry whatever is already chosen above into the popup, before it opens. */
    syncFromPage() {
      var checked = null;
      this.pageInputs.forEach(function (input) {
        if (input.checked) checked = input.value;
      });
      if (checked === null) return;
      this.querySelectorAll('[data-set-addon-this-input]').forEach(function (input) {
        input.checked = input.value === checked;
      });
    }

    /** One-way mirror back to the page, so the two can never disagree. */
    syncToPage(value) {
      var target = null;
      this.pageInputs.forEach(function (input) {
        if (!target && input.value === value) target = input;
      });
      if (!target || target.checked) return;
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* `syncPreorder()` is called from here rather than from each of the three
       call sites, so a future one cannot forget it. */
    syncReadouts() {
      var a = this.querySelector('[data-set-addon-this-input]:checked');
      var b = this.querySelector('[data-set-addon-input]:checked');
      if (this.thisChosenNode) this.thisChosenNode.textContent = a ? a.value : '';
      if (this.partnerChosenNode) this.partnerChosenNode.textContent = b ? b.value : '';
      this.syncPreorder();
    }

    setOpen(open) {
      if (open) this.syncFromPage();
      /* No `hidden` any more: the panel animates from `grid-template-rows: 0fr`
         to `1fr`, and a hidden element has no rows to animate. `data-open` on
         the host drives the whole thing from product.css. */
      this.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.toggleAttribute('data-open', open);
      if (!open) return;
      this.syncReadouts();

      /* Bring the card into view as it grows. `nearest` rather than `center`,
         so a card already fully on screen does not move at all — scrolling a
         shopper who can already see what she tapped is disorienting. The frame
         of delay lets the height transition start first, so what she follows
         is the card opening rather than the page jumping. */
      var element = this;
      var behavior = prefersReducedMotion() ? 'auto' : 'smooth';
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(function () {
          element.scrollIntoView({ block: 'nearest', behavior: behavior });
        });
      }

      /* Land on the first size still to choose, not on the panel edge.
         `preventScroll` because the scroll above already owns that. */
      var pending = this.querySelector('[data-set-addon-this-input]:checked')
        ? '[data-set-addon-input]'
        : '[data-set-addon-this-input]';
      var first = this.querySelector(pending + ':not([data-unavailable])');
      if (first) first.focus({ preventScroll: true });
    }

    /**
     * Label and state together, because they are one thing:
     * `.pdpdark-atc:not([data-state='idle'])` is what hides the rule and the
     * price, so a button that says "Adding" must not still be quoting $148.
     * Same rule, same reason, as the ADD TO CART button above it.
     */
    /**
     * The set's pre-order state, from whichever of the two pieces is one.
     *
     * The same single lookup the product page uses, against the same
     * server-rendered index — the set partner and every colour of it are in it
     * for exactly this reason. It used to re-derive the answer from an
     * `oversell` flag this snippet stamped onto its own copy of the variants,
     * which is a second definition of "pre-order" living next to the real one
     * and drifting from it.
     *
     * If BOTH pieces are pre-orders the LATER ship date wins the line, because
     * that is when the set actually turns up. A shopper told the earlier date
     * would be told something untrue about half of what she bought.
     */
    preorderState() {
      var pieces = [this.chosenThis, this.chosen].filter(Boolean);
      var found = [];
      pieces.forEach(function (piece) {
        var entry = preorderFor(piece.id);
        if (entry) found.push({ piece: piece, entry: entry });
      });
      if (found.length === 0) return null;

      /* One line, and it has to describe the whole set. The dates sort
         correctly as text only because they are the same sentence with a
         different date in it, which is what the app produces; `sort().pop()`
         is therefore "the last one alphabetically", not a date comparison, and
         a merchant writing two differently-worded messages would get the one
         that sorts last rather than the one that ships last. Both campaigns on
         this store share the wording. */
      var message = found
        .map(function (f) { return f.entry.message; })
        .filter(Boolean)
        .sort()
        .pop();

      return {
        label: preorderCopy().buttonText,
        message: message || '',
        /* The cart line each pre-ordered piece has to become — the same
           `properties[Note] = "PRE-ORDER"` the product page's own add writes,
           keyed by piece because a set can be one in-stock piece and one
           pre-order. Without this the set's two lines reached the checkout
           looking like ordinary stock while the same garment bought on its own
           said PRE-ORDER. */
        lines: found.reduce(function (acc, f) {
          var line = preorderLine(f.piece);
          if (line) acc[f.piece.id] = line;
          return acc;
        }, {}),
      };
    }

    /** Button wording and the ship-by line, recomputed on every size change. */
    syncPreorder() {
      var state = this.preorderState();
      if (this.preorderNode) {
        this.preorderNode.textContent = state ? state.message : '';
        this.preorderNode.hidden = !(state && state.message);
      }
      if (this.addButton && this.addButton.getAttribute('data-state') === 'idle') {
        this.setAddState('idle', state ? state.label : 'Add to cart');
      }
    }

    setAddState(state, text) {
      if (this.addButton) this.addButton.setAttribute('data-state', state);
      if (this.addText) this.addText.textContent = text;
    }

    setError(message) {
      if (!this.errorNode) return;
      this.errorNode.textContent = message || '';
      this.errorNode.toggleAttribute('hidden', !message);
    }

    handleKeydown(event) {
      if (event.key !== 'Escape' || !this.open) return;
      this.setOpen(false);
      this.trigger.focus({ preventScroll: true });
    }

    handleChange(event) {
      if (!event.target.matches('[data-set-addon-input], [data-set-addon-this-input]')) return;
      if (event.target.matches('[data-set-addon-this-input]')) {
        this.syncToPage(event.target.value);
      }
      this.syncReadouts();
      this.setError('');
    }

    handleClick(event) {
      if (event.target.closest('[data-set-addon-open]')) {
        this.setOpen(!this.open);
        return;
      }
      if (event.target.closest('[data-set-addon-close]')) {
        this.setOpen(false);
        this.trigger.focus({ preventScroll: true });
        return;
      }
      if (event.target.closest('[data-set-addon-add]')) this.add();
    }

    add() {
      if (this.pending) return;

      var mine = this.chosenThis;
      var variant = this.chosen;

      var missing = null;
      if (!mine) missing = '[data-set-addon-this-input]';
      else if (!variant) missing = '[data-set-addon-input]';
      if (missing) {
        this.setError('Please choose a size for both pieces.');
        var first = this.querySelector(missing + ':not([data-unavailable])');
        if (first) first.focus({ preventScroll: true });
        return;
      }
      var preorder = this.preorderState();
      var lines = (preorder && preorder.lines) || {};

      /* `isBuyable` rather than `available`, so a pre-order piece is never
         turned away by its own stock count. It is the same gate the product
         page's own ADD TO CART uses, so the two cannot disagree about whether
         a size can be bought. */
      if (!isBuyable(mine) || !isBuyable(variant)) {
        this.setError('That size is sold out.');
        return;
      }

      this.pending = true;
      this.setError('');
      this.setAddState('loading', 'Adding');

      var element = this;
      fetch(cartRoute('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        /* One request, two lines. Two sequential adds can half-succeed, and a
           shopper who was promised a set and got one piece is a support
           email. */
        /* A pre-ordered piece carries the campaign's line item properties, and
           its selling plan when the campaign calls for one, exactly as the
           product page's own add does. Without them Shopify records an ordinary
           purchase and neither the cart nor the checkout knows the set contains
           something that has not been made yet. Each piece is looked up on its
           own, because a set can be one in-stock piece and one pre-order. */
        body: JSON.stringify({
          items: [mine, variant].map(function (piece) {
            var item = { id: piece.id, quantity: 1 };
            var line = lines[piece.id];
            if (line) {
              if (Object.keys(line.properties).length > 0) item.properties = line.properties;
              if (line.sellingPlan) item.selling_plan = line.sellingPlan;
            }
            return item;
          }),
        }),
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(function (item) {
          return fetch(cartRoute('cart.js'), { headers: { Accept: 'application/json' } })
            .then(function (response) {
              return response.json();
            })
            .catch(function () {
              return null;
            })
            .then(function (cart) {
              if (cart) updateCartCount(cart.item_count);
              element.announce(variant, item, cart);
            });
        })
        .catch(function (error) {
          element.setAddState('idle', 'Add to cart');
          element.setError(
            (error && (error.description || error.message)) ||
              'Sorry, we could not add that to your cart.'
          );
        })
        .then(function () {
          element.pending = false;
        });
    }

    announce(variant, item, cart) {
      var element = this;
      this.setAddState('added', 'Added');
      this.setOpen(false);

      if (this.resetTimer) clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(function () {
        element.setAddState('idle', 'Add to cart');
      }, 2400);

      /* The same event every other add on this page emits, so the cart drawer
         opens and the header count updates with no wiring of its own. */
      this.dispatchEvent(
        new CustomEvent('kosha:cart:added', {
          bubbles: true,
          composed: true,
          detail: { variant: variant, item: item, cart: cart, source: 'set-addon' },
        })
      );
    }
  }

  if (!customElements.get('kosha-set-addon')) {
    customElements.define('kosha-set-addon', KoshaSetAddon);
  }

  /* ==========================================================================
     SIZE GUIDE DRAWER + DESCRIPTION CLAMP
     Small enough not to warrant custom elements of their own.

     The size guide is an <aside> that slides in from the inline-end edge (see
     the doc block in snippets/size-guide-modal.liquid for why not a <dialog>),
     so open/close is `data-open` plus four things done by hand: the scroll
     lock, a focus trap, Escape, and returning focus to the trigger.

     SCROLL LOCK — the hard part, and the reason this reads longer than it
     looks. `html.kosha-scroll-lock` is declared in assets/cart-drawer.css and
     is normally driven by a depth counter inside assets/global.js's IIFE.
     That counter is a closure variable. The consolidation round gave global.js
     a published `window.kosha` surface, but deliberately did NOT put the lock
     counter on it: sharing the counter would change live scroll-lock
     behaviour, and this round's mandate was zero behaviour change. It is the
     obvious next cleanup and is recorded as such in the consolidation report.
     Until then there are still two independent counters, and they can desync
     in exactly two directions:

       A. size guide closes while the cart is still open  → guarded. `unlock()`
          refuses to drop the class while any `[data-cart-drawer][data-open]`
          or `[data-search-overlay][data-open]` is present, i.e. while
          global.js's own counter is still non-zero.
       B. the cart closes while the size guide is still open → global.js would
          drop the class unconditionally, and this file cannot stop it. So the
          case is designed out instead: the size guide closes itself on
          `kosha:cart:added`, before <kosha-cart-drawer> auto-opens. The two
          drawers never coexist, which is also the right call visually — they
          are both right-side sheets.

     Nothing else can open the cart behind an open size guide: the overlay
     covers the header (and a click on it closes this drawer) and Tab is
     trapped inside the panel.
     ========================================================================== */

  var SIZE_GUIDE_FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),' +
    ' textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* The trigger this drawer was opened from, so focus can be handed back on
     close. One PDP renders one size guide, so one variable is enough. */
  var sizeGuideTrigger = null;
  var sizeGuideLocked = false;

  function sizeGuidePanelFor(node) {
    var root = node.closest('[data-product-info]') || document;
    return root.querySelector('[data-size-guide]') || document.querySelector('[data-size-guide]');
  }

  /**
   * Move the drawer to <body>, where the cart drawer already lives.
   *
   * Liquid renders it inside `product-info.liquid`, i.e. inside
   * `.product__info` — which is `position: sticky` at >=990px. **Sticky
   * positioning always creates a stacking context**, z-index or not, so a
   * `position: fixed` child is trapped inside it: `.size-guide`'s z-index 88
   * is resolved against `.product__info` (itself z-index auto), and
   * `.header`'s z-index 50 at the root wins. Measured at 1280x900 — the site
   * header painted over the top 77px of the panel, swallowing the title and
   * the close button's hit area, while 500px was correct because the info
   * column is not sticky below 990px.
   *
   * The old `<dialog>` was immune only because `showModal()` promotes to the
   * top layer, which ignores stacking contexts entirely. Nothing else does, so
   * the element has to leave the sticky subtree. `snippets/product-info.liquid`
   * is outside this lane's file boundary, hence the hoist here rather than a
   * different render site.
   */
  function hoistSizeGuide() {
    document.querySelectorAll('[data-size-guide]').forEach(function (guide) {
      if (guide.parentElement !== document.body) document.body.appendChild(guide);
    });
  }

  function sizeGuideLock() {
    if (sizeGuideLocked) return;
    sizeGuideLocked = true;
    /* NO SCROLLBAR MEASUREMENT. This used to read
       `window.innerWidth - documentElement.clientWidth` and stamp the result on
       <html> as `--kosha-scrollbar-w`.

       global.js dropped the same code, and cart-drawer.css records why: the
       value was subtracted from the width of every section on the page, so one
       bad reading squeezed the whole site into a narrow column with the rest of
       the window blank — and because it was written as an inline style it
       survived until the next unlock. That reading goes wrong exactly when the
       two numbers stop agreeing, which is what browser zoom does to them.

       Nothing consumes the property now, so this was writing a variable with a
       whole-page failure mode for no benefit at all. */
    if (document.documentElement.classList.contains('kosha-scroll-lock')) return;
    document.documentElement.classList.add('kosha-scroll-lock');
  }

  function sizeGuideUnlock() {
    if (!sizeGuideLocked) return;
    sizeGuideLocked = false;
    /* Direction A above: another overlay still holds the lock, so leave the
       class alone and let global.js's counter release it. */
    if (document.querySelector('[data-cart-drawer][data-open], [data-search-overlay][data-open]')) {
      return;
    }
    document.documentElement.classList.remove('kosha-scroll-lock');
  }

  function sizeGuideFocusable(panel) {
    if (!panel) return [];
    return Array.prototype.filter.call(panel.querySelectorAll(SIZE_GUIDE_FOCUSABLE), function (node) {
      return node.offsetWidth > 0 || node.offsetHeight > 0 || node === document.activeElement;
    });
  }

  function sizeGuideKeydown(event) {
    var guide = document.querySelector('[data-size-guide][data-open]');
    if (!guide) return;

    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      event.stopPropagation();
      closeSizeGuide(guide);
      return;
    }
    if (event.key !== 'Tab') return;

    var panel = guide.querySelector('[data-size-guide-panel]') || guide;
    /* Recomputed on every Tab rather than cached at open time — the chart is
       merchant-authored RTE content and can contain links. */
    var nodes = sizeGuideFocusable(panel);
    if (nodes.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    var active = document.activeElement;

    if (!panel.contains(active)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openSizeGuide(guide, trigger) {
    if (!guide || guide.hasAttribute('data-open')) return;
    sizeGuideTrigger = trigger || null;
    guide.setAttribute('data-open', '');
    sizeGuideLock();
    document.addEventListener('keydown', sizeGuideKeydown, true);

    var target = guide.querySelector('[data-size-guide-close]');
    window.requestAnimationFrame(function () {
      if (guide.hasAttribute('data-open') && target) target.focus();
    });
  }

  function closeSizeGuide(guide) {
    if (!guide || !guide.hasAttribute('data-open')) return;
    guide.removeAttribute('data-open');
    sizeGuideUnlock();
    document.removeEventListener('keydown', sizeGuideKeydown, true);

    var trigger = sizeGuideTrigger;
    sizeGuideTrigger = null;
    if (trigger && document.contains(trigger) && typeof trigger.focus === 'function') {
      trigger.focus();
    }
  }

  document.addEventListener('click', function (event) {
    if (!event.target || typeof event.target.closest !== 'function') return;

    var opener = event.target.closest('[data-size-guide-open]');
    if (opener) {
      var guide = sizeGuidePanelFor(opener);
      if (guide) {
        event.preventDefault();
        /* Belt and braces: init() already hoisted, but a size guide added by a
           later section render would still be inside the sticky column. */
        hoistSizeGuide();
        openSizeGuide(guide, opener);
      }
      return;
    }

    var close = event.target.closest('[data-size-guide-close]');
    if (close) {
      closeSizeGuide(close.closest('[data-size-guide]'));
      return;
    }

    /* Overlay click. The overlay is its own element rather than the <aside>
       (which is inset:0 and would swallow clicks on the panel's own padding
       through event bubbling if it were the hit target). */
    var overlay = event.target.closest('[data-size-guide-overlay]');
    if (overlay) closeSizeGuide(overlay.closest('[data-size-guide]'));
  });

  /* Direction B, designed out: close before <kosha-cart-drawer> auto-opens on
     the same event, so two right-side sheets never stack and global.js's
     unconditional unlock can never fire underneath an open size guide. */
  document.addEventListener('kosha:cart:added', function () {
    closeSizeGuide(document.querySelector('[data-size-guide][data-open]'));
  });

  /* The size guide's tabs and its Find My Size calculator moved to
     assets/size-guide.js on 2026-09-04, when /pages/size-chart started
     rendering the same guide. That page has no reason to load this file, and
     this file has no reason to know about a page. What stays here is the
     DRAWER: opening it, trapping focus in it and closing it are product-page
     concerns. */

  /**
   * Description read-more. The clamp is CSS (`-webkit-line-clamp`); this only
   * decides whether the toggle is worth showing. Measured on load and again on
   * every `toggle` of the surrounding <details>, because a closed <details>
   * reports zero height.
   */
  function syncClamp(clamp) {
    var panel = clamp.closest('.accordion__panel');
    var toggle = panel ? panel.querySelector('[data-clamp-toggle]') : null;
    if (!toggle) return;
    if (clamp.hasAttribute('data-clamp-expanded')) return;
    toggle.hidden = clamp.scrollHeight <= clamp.clientHeight + 4;
  }

  function initClamps() {
    document.querySelectorAll('[data-clamp]').forEach(function (clamp) {
      syncClamp(clamp);
      var details = clamp.closest('details');
      if (details) {
        details.addEventListener('toggle', function () {
          if (details.open) syncClamp(clamp);
        });
      }
    });
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-clamp-toggle]');
    if (!toggle) return;
    var panel = toggle.closest('.accordion__panel');
    var clamp = panel ? panel.querySelector('[data-clamp]') : null;
    if (!clamp) return;
    var expanded = clamp.toggleAttribute('data-clamp-expanded');
    toggle.textContent = expanded ? 'Read less' : 'Read more';
  });

  /* ==========================================================================
     BOTTOM-EDGE OBSTRUCTIONS  (`--kosha-bottom-obstruction`)

     Two Shopify apps now squat on the bottom edge of this store's pages: the
     Klaviyo sign-up teaser and Postscript's "Text me when it restocks" banner,
     the second of which is full-width and lands squarely under the sticky
     add-to-cart card. §2k of product.css argued against keying a CSS rule off
     a third party's class name, and that argument still holds — a selector
     guessed from a screenshot stops matching on the app's next release.

     So this measures instead of guessing. It looks for anything pinned to the
     viewport's bottom edge that this theme did not put there, takes the
     tallest, and publishes its height as a custom property on <html>. The
     sticky card and its size picker add that number to their own bottom
     offsets, so the card rides above whatever is there — including apps this
     theme has never heard of.

     WHAT COUNTS AS AN OBSTRUCTION, and why each half of the test is needed:

       - a DIRECT CHILD OF <body>. App widgets are injected at the end of the
         document; every part of this theme is inside <main>, a
         `<section class="shopify-section">`, or one of the four elements this
         theme hoists to <body> itself (the sticky card, the size-guide drawer,
         the cart drawer, the search overlay). The allow-list below names them,
         so a theme element can never be mistaken for a squatter.
       - `position: fixed` or `sticky`. A static element at the end of <body>
         is not over anything.
       - its bottom within 4px of the viewport's bottom, and its top inside the
         viewport. A cookie bar at the top or a chat bubble halfway up is not
         in the card's way.
       - height <= 200px. Past that it is not a banner, it is a takeover, and
         chasing it would push the card off the screen. Logged once and
         ignored, per the brief.

     COST. Bounded and deliberate: `document.body.children` is a short list,
     and the scan runs on a body mutation, on resize, and on a 1.2s interval
     that only ticks while the card is actually up. No hot loop, and nothing at
     all on a page with no sticky card.
     ========================================================================== */

  var OBSTRUCTION_MAX = 200;
  var OBSTRUCTION_THEME = [
    'kosha-sticky-atc',
    '[data-size-guide]',
    '[data-cart-drawer]',
    '[data-search-overlay]',
    '.shopify-section',
    'main',
    'header',
    'footer',
    'dialog',
    'script',
    'style',
    'link',
    'template',
    'noscript',
    '.skip-to-content',
  ].join(',');

  var obstructionCurrent = -1;
  var obstructionWarned = false;
  var obstructionTimer = 0;

  function measureBottomObstruction() {
    var viewport = window.innerHeight || document.documentElement.clientHeight || 0;
    var tallest = 0;
    var children = document.body ? document.body.children : [];

    for (var i = 0; i < children.length; i += 1) {
      var node = children[i];
      if (node.nodeType !== 1) continue;
      if (node.matches(OBSTRUCTION_THEME)) continue;
      if (node.hasAttribute('hidden')) continue;

      var style = window.getComputedStyle(node);
      if (style.position !== 'fixed' && style.position !== 'sticky') continue;
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      if (parseFloat(style.opacity) === 0) continue;

      var rect = node.getBoundingClientRect();
      if (rect.height < 8 || rect.width < 8) continue;
      if (rect.bottom < viewport - 4 || rect.bottom > viewport + 4) continue;
      if (rect.top >= viewport) continue;

      if (rect.height > OBSTRUCTION_MAX) {
        if (!obstructionWarned) {
          obstructionWarned = true;
          /* Deliberately visible. A 200px+ bottom banner is a real problem
             with the app's configuration, and staying silent about it would
             leave the next person wondering why the card did not move. */
          console.warn(
            'kosha: bottom obstruction ' +
              Math.round(rect.height) +
              'px exceeds the ' +
              OBSTRUCTION_MAX +
              'px lift cap; sticky bar left in place.'
          );
        }
        continue;
      }

      if (rect.height > tallest) tallest = rect.height;
    }

    var next = Math.round(tallest);
    if (next === obstructionCurrent) return;
    obstructionCurrent = next;
    if (next > 0) {
      document.documentElement.style.setProperty('--kosha-bottom-obstruction', next + 'px');
    } else {
      document.documentElement.style.removeProperty('--kosha-bottom-obstruction');
    }
  }

  function initBottomObstruction() {
    var bar = document.querySelector('kosha-sticky-atc');
    if (!bar || !document.body) return;

    measureBottomObstruction();

    if ('MutationObserver' in window) {
      new MutationObserver(measureBottomObstruction).observe(document.body, {
        childList: true,
      });
    }
    window.addEventListener('resize', measureBottomObstruction, { passive: true });

    /* An app can also change its own banner's height in place, which a
       childList observer never sees. A slow poll covers that, and only while
       the card it protects is on screen. */
    obstructionTimer = window.setInterval(function () {
      if (document.hidden) return;
      var host = document.querySelector('kosha-sticky-atc');
      if (!host || host.hidden) return;
      measureBottomObstruction();
    }, 1200);
  }

  function init() {
    /* seedCartCount() used to run here. assets/global.js runs the identical
       seed at the end of its own IIFE on every page, so this call was doing
       the same idempotent work a second time. collection.js dropped its copy
       for the same reason. */
    initClamps();
    hoistSizeGuide();
    initBottomObstruction();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
